/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import WaveSurfer from "wavesurfer.js";
import {
  Play, Pause, SkipBack, Upload, Maximize2, Music2, Headphones, RotateCcw,
  Radio, Square, X, AlertTriangle, ExternalLink, Disc3, Download, Plus,
  Sliders, Palette, Layout as LayoutIcon, Target,
} from "lucide-react";
import { LocalAudioTrack, Track, ConnectionState, RoomEvent } from "livekit-client";
import { DjEngine } from "../audio/DjEngine";
import { RemoteEngine } from "../audio/RemoteEngine";
import { hashFile, saveTrack, listGhosts, getByHash, removeGhost } from "../lib/djTrackCache";
import { captureStageInAudio, isStageInSupported, isMac, isSafari, isMobile } from "../lib/stageIn";
import AutoDjPanel from "./dj/AutoDjPanel";
import FxRack from "./dj/FxRack";
import LibraryPanel from "./dj/LibraryPanel";
import MirroredWaveform from "./dj/MirroredWaveform";
import { useLibrary } from "../audio/useLibrary";
import { useRecorder } from "../audio/useRecorder";
import "../styles/dj-skins.css";

const BC_CHANNEL = "stackroom-dj";

// Phase A R3 — Tier system replaces skins + layouts.
// Each tier unlocks a progressively larger feature surface. Tier picker
// is the single dropdown in the toolbar; default Tier 3.
//
// Convenience flag helpers per-tier are computed below as constants.
const TIERS = [
  { key: 1, label: "1 · Basic 2-Deck" },
  { key: 2, label: "2 · 2-Deck + FX" },
  { key: 3, label: "3 · 2-Deck + Full" },
  { key: 4, label: "4 · 4-Deck" },
  { key: 5, label: "5 · Performance Pad" },
];
const TIER_HAS_FX        = (t) => t >= 2;
const TIER_HAS_CUES_LOOP = (t) => t >= 3;
const TIER_HAS_4DECK     = (t) => t === 4;
const TIER_HAS_PERF      = (t) => t === 5;
// LAYOUTS retained as an internal name so the rest of the code that
// references `activeLayout` ("classic2" | "pro4" | "perf") still works.
// We derive activeLayout from the tier instead of asking the user twice.
const tierLayoutKey = (t) => t === 4 ? "pro4" : t === 5 ? "perf" : "classic2";

export default function DjConsole2({ room, onNowPlaying, mini = false, layout = "fixed", externalEngine = null, mode = "inline", remoteRoomId = null }) {
  // Phase 3.6 — engine selection.
  //   mode="popout"  → talk to the main tab's engine over BroadcastChannel
  //                    via a RemoteEngine that mirrors the DjEngine API.
  //   externalEngine → parent (useDjEngine in Room.jsx) owns the engine.
  //                    We borrow it for direct, zero-latency UI access.
  //   otherwise      → self-instantiate (legacy / standalone case).
  const isPopout = mode === "popout";
  const engineRef = useRef(externalEngine?.current || null);
  const ownsEngine = isPopout || !externalEngine;
  const publishedRef = useRef(false);
  const musicLkRef = useRef(null);       // LocalAudioTrack wrapping engine.broadcastDest
  const stageInLkRef = useRef(null);     // LocalAudioTrack wrapping Share Audio
  const stageInStopRef = useRef(null);   // cleanup fn from captureStageInAudio
  const stageInAnalyserRef = useRef(null); // AnalyserNode tapped off Share Audio stream
  const wsARef = useRef(null);
  const wsBRef = useRef(null);
  const containerARef = useRef(null);
  const containerBRef = useRef(null);
  const fileInputRef = useRef(null);
  const ghostInputRef = useRef(null);
  const bcRef = useRef(null);
  const outerRef = useRef(null);
  const pendingMetaRef = useRef(null); // queued "now playing" while not yet Connected
  const recorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const recStartedAtRef = useRef(0);
  const pendingGhostAttachRef = useRef(null); // ghost hash to be matched

  const [tracks, setTracks] = useState([]);
  const [deckA, setDeckA] = useState({ title: "—", playing: false, pos: 0, dur: 0, bpm: 0, pitch: 0, hotCues: [null,null,null,null,null,null,null,null], loop: {start:null,end:null,on:false} });
  const [deckB, setDeckB] = useState({ title: "—", playing: false, pos: 0, dur: 0, bpm: 0, pitch: 0, hotCues: [null,null,null,null,null,null,null,null], loop: {start:null,end:null,on:false} });
  // Phase A R2 — decks C and D for Pro 4-Deck layout. Always allocated;
  // only rendered when activeLayout === "pro4".
  const [deckC, setDeckC] = useState({ title: "—", playing: false, pos: 0, dur: 0, bpm: 0, pitch: 0, hotCues: [null,null,null,null,null,null,null,null], loop: {start:null,end:null,on:false} });
  const [deckD, setDeckD] = useState({ title: "—", playing: false, pos: 0, dur: 0, bpm: 0, pitch: 0, hotCues: [null,null,null,null,null,null,null,null], loop: {start:null,end:null,on:false} });
  // Phase A R2 — FX rack UI state, mirrors engine.fxRack.getState().
  const [fxState, setFxState] = useState({});
  // Phase A R2 — Per-deck crossfader assign (A / B / thru). Default A→A, B→B, C→A, D→B.
  const [xfaderAssigns, setXfaderAssigns] = useState({ A: "A", B: "B", C: "A", D: "B" });
  const [xfader, setXfader] = useState(0);
  const [master, setMaster] = useState(0.8);
  const [vu, setVu] = useState({ rms: 0, peak: 0 });
  const [dragOver, setDragOver] = useState(false);
  const [cueA, setCueA] = useState(false);
  const [cueB, setCueB] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const [compact, setCompact] = useState(false); // Phase 3.5 — ≤640px tab-switcher mode
  const [compactTab, setCompactTab] = useState("A"); // A | B | mixer | library
  // Phase A R3 — Tier picker replaces skin + layout. Default Tier 3
  // (2-Deck + Full features). Persisted in localStorage as `sr-dj-tier`.
  const [tier, setTier] = useState(() => {
    try { return parseInt(localStorage.getItem("sr-dj-tier") || "", 10) || 3; }
    catch { return 3; }
  });
  useEffect(() => { try { localStorage.setItem("sr-dj-tier", String(tier)); } catch {} }, [tier]);
  const activeLayout = tierLayoutKey(tier);
  const hasFx        = TIER_HAS_FX(tier);
  const hasCuesLoop  = TIER_HAS_CUES_LOOP(tier);

  // Phase A R3 — Music library + Auto-DJ playlist + Recorder.
  const library = useLibrary();
  const recorder = useRecorder({ engineRef });
  // Phase A R5 followup — Auto-DJ playlist lives on the engine (single
  // source of truth). The local state mirrors `engine.autoDj.playlist`
  // and re-renders on every engine emit. localStorage persistence happens
  // engine-side via useDjEngine on every change.
  const [autoDjPlaylist, setAutoDjPlaylist] = useState(() => engineRef.current?.autoDj?.playlist || []);
  // Phase A R8 B1 — the listener subscription used to live in a SEPARATE
  // useEffect here with `[]` deps. React ran it BEFORE the engine-creation
  // useEffect below, so `engineRef.current` was still null and the
  // subscription early-returned. That's why every "+" click looked fine in
  // logs but never actually populated the popout's React state (the
  // optimistic mirror's listener array was empty). The fix lives inside
  // the engine-creation useEffect now so subscription order is guaranteed.

  // Set-playlist callback proxied through engine so both UIs stay in sync.
  const updatePlaylist = (next) => {
    const eng = engineRef.current; if (!eng?.setAutoDj) return;
    const list = typeof next === "function" ? next(autoDjPlaylist) : next;
    eng.setAutoDj({ playlist: list });
  };
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Phase 2.1 state
  const [stageInOn, setStageInOn] = useState(false);
  const [stageInLabel, setStageInLabel] = useState("");
  const [npOverride, setNpOverride] = useState(() => {
    try { return localStorage.getItem("sr-dj-np-override") || ""; } catch { return ""; }
  });
  const [recordingOn, setRecordingOn] = useState(false);
  const [recElapsed, setRecElapsed] = useState(0);
  const [recResult, setRecResult] = useState(null); // { blob, url, filename, bytes, durationMs }
  const [ghosts, setGhosts] = useState([]);
  const [showSafariModal, setShowSafariModal] = useState(false);
  const [showMacTip, setShowMacTip] = useState(false);
  const [stageInError, setStageInError] = useState("");
  // Phase 2.2.x — mobile-block modal for Share Audio (getDisplayMedia is unavailable
  // on iOS/Android browsers). Computed once at module load — no react re-render
  // dependency needed.
  const [showMobileBlockModal, setShowMobileBlockModal] = useState(false);
  const stageInOnMobile = isMobile();

  useEffect(() => {
    // Phase 3.6 R2 — engine selection.
    //   popout         → fresh RemoteEngine over BroadcastChannel
    //   externalEngine → borrow from parent (no self-create, no self-dispose)
    //   else           → self-create local DjEngine (legacy)
    let eng;
    if (isPopout) {
      eng = new RemoteEngine(remoteRoomId);
    } else if (externalEngine?.current) {
      eng = externalEngine.current;
    } else {
      eng = new DjEngine();
    }
    engineRef.current = eng;
    // Phase A R8 B1 — subscribe to autodj state changes RIGHT AFTER engineRef
    // is set, in the SAME effect. The previous structure used a separate
    // useEffect declared earlier in the file; React ran it first (with
    // engineRef.current=null) so the subscription early-returned and the
    // popout's React state never received "+"-add notifications. Now the
    // subscription is colocated with engine creation — order is guaranteed.
    setAutoDjPlaylist(eng.autoDj?.playlist || []);
    // eslint-disable-next-line no-console
    console.log(`[DjConsole2] autoDj listener subscribing on ${isPopout ? "RemoteEngine" : "DjEngine"}; initial playlist len=${eng.autoDj?.playlist?.length || 0}`);
    const offAutoDjSub = eng.onAutoDj?.((state) => {
      // eslint-disable-next-line no-console
      console.log(`[DjConsole2] autoDj listener received playlist len=${state?.playlist?.length || 0}`);
      setAutoDjPlaylist(state.playlist || []);
    });
    bcRef.current = new BroadcastChannel(BC_CHANNEL);
    bcRef.current.onmessage = (e) => {
      const m = e.data; if (!m) return;
      if (m.type === "xfader") { eng.setCrossfader(m.value); setXfader(m.value); }
      else if (m.type === "play") (m.deck === "A" ? eng.deckA : eng.deckB).play();
      else if (m.type === "pause") (m.deck === "A" ? eng.deckA : eng.deckB).pause();
    };

    eng.deckA.on("positionchange", (t) => setDeckA(s => ({ ...s, pos: t, playing: eng.deckA.playing })));
    eng.deckB.on("positionchange", (t) => setDeckB(s => ({ ...s, pos: t, playing: eng.deckB.playing })));
    eng.deckC?.on?.("positionchange", (t) => setDeckC(s => ({ ...s, pos: t, playing: eng.deckC.playing })));
    eng.deckD?.on?.("positionchange", (t) => setDeckD(s => ({ ...s, pos: t, playing: eng.deckD.playing })));

    // Phase 3.6 — if the engine is loaded from elsewhere (e.g. the DJ board
    // pop-out shipping a File via BroadcastChannel), reflect the new deck
    // metadata locally so the inline title/duration/hot-cues match.
    // R6 B2: reset hasBands on new track so MirroredWaveform recomputes.
    const onLoadedA = ({ duration, title }) => setDeckA(s => ({
      ...s, title, dur: duration, pos: 0, hotCues: [...eng.deckA.hotCues],
      loop: { start: null, end: null, on: false }, hasBands: false,
    }));
    const onLoadedB = ({ duration, title }) => setDeckB(s => ({
      ...s, title, dur: duration, pos: 0, hotCues: [...eng.deckB.hotCues],
      loop: { start: null, end: null, on: false }, hasBands: false,
    }));
    const onLoadedC = ({ duration, title }) => setDeckC(s => ({
      ...s, title, dur: duration, pos: 0, hotCues: [...(eng.deckC?.hotCues || [])],
      loop: { start: null, end: null, on: false }, hasBands: false,
    }));
    const onLoadedD = ({ duration, title }) => setDeckD(s => ({
      ...s, title, dur: duration, pos: 0, hotCues: [...(eng.deckD?.hotCues || [])],
      loop: { start: null, end: null, on: false }, hasBands: false,
    }));
    const onBpmA = (bpm) => { setDeckA(s => ({ ...s, bpm })); tryAutoSync("A"); };
    const onBpmB = (bpm) => { setDeckB(s => ({ ...s, bpm })); tryAutoSync("B"); };
    const onBpmC = (bpm) => setDeckC(s => ({ ...s, bpm }));
    const onBpmD = (bpm) => setDeckD(s => ({ ...s, bpm }));
    eng.deckA.on("loaded", onLoadedA);
    eng.deckB.on("loaded", onLoadedB);
    eng.deckC?.on?.("loaded", onLoadedC);
    eng.deckD?.on?.("loaded", onLoadedD);
    eng.deckA.on("bpm", onBpmA);
    eng.deckB.on("bpm", onBpmB);
    eng.deckC?.on?.("bpm", onBpmC);
    eng.deckD?.on?.("bpm", onBpmD);
    // Phase A R6 B2 — flip state.hasBands true so MirroredWaveform's
    // useBands re-runs and reads the cached band data from the deck.
    const onBandsA = () => setDeckA(s => ({ ...s, hasBands: true }));
    const onBandsB = () => setDeckB(s => ({ ...s, hasBands: true }));
    eng.deckA.on("bands-ready", onBandsA);
    eng.deckB.on("bands-ready", onBandsB);
    // Seed initial FX state from engine
    if (eng.fxRack?.getState) {
      try { setFxState(eng.fxRack.getState()); } catch { /* */ }
    }

    let rafId;
    const loop = () => {
      const sa = stageInAnalyserRef.current;
      if (sa) {
        // VU off the captured Share Audio stream
        const buf = new Uint8Array(sa.frequencyBinCount);
        sa.getByteTimeDomainData(buf);
        let s = 0, p = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          s += v * v; const a = Math.abs(v); if (a > p) p = a;
        }
        setVu({ rms: Math.sqrt(s / buf.length), peak: p });
      } else {
        setVu(eng.readVU());
      }
      setDeckA(s => ({ ...s, playing: eng.deckA.playing }));
      setDeckB(s => ({ ...s, playing: eng.deckB.playing }));
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    (async () => {
      await eng.resume();
      // When the engine is owned externally (useDjEngine in Room.jsx), the
      // owner handles the LiveKit publish — don't double-publish here.
      // Pop-out mode never publishes; it has no LiveKit room object.
      if (!ownsEngine || isPopout) return;
      if (!room) return;

      const tryPublish = async () => {
        if (publishedRef.current) return;
        try {
          const mst = eng.getBroadcastTrack();
          if (!mst) return;
          const lkTrack = new LocalAudioTrack(mst);
          await room.localParticipant.publishTrack(lkTrack, {
            name: "DJ Console Master", source: Track.Source.Microphone,
          });
          publishedRef.current = true;
          musicLkRef.current = lkTrack;
        } catch (err) {
          console.warn("DJ publish failed (non-fatal):", err);
        }
      };

      const flushPendingMeta = () => {
        const meta = pendingMetaRef.current;
        if (!meta) return;
        pendingMetaRef.current = null;
        try {
          room.localParticipant.setMetadata(JSON.stringify(meta));
        } catch (e) {
          console.warn("setMetadata flush failed (non-fatal):", e);
        }
      };

      if (room.state === ConnectionState.Connected) {
        await tryPublish();
        flushPendingMeta();
      } else {
        const onConnected = async () => {
          await tryPublish();
          flushPendingMeta();
        };
        room.once(RoomEvent.Connected, onConnected);
      }
    })();

    return () => {
      cancelAnimationFrame(rafId);
      try { bcRef.current?.close(); } catch {/**/}
      // Phase A R8 B1 — unsubscribe the autodj listener installed at the
      // top of this same effect.
      try { offAutoDjSub?.(); } catch {/**/}
      // Pop-out's RemoteEngine and self-created engines are ours to dispose.
      // External engines (owned by Room.jsx) are not.
      if (isPopout || ownsEngine) {
        try { eng.dispose(); } catch {/**/}
      }
    };
  }, []);

  useEffect(() => {
    if (!containerARef.current || wsARef.current) return;
    // Phase A R10 A — KILL the per-deck WaveSurfer animation. The
    // MirroredWaveform (rewritten in R9 F as a scrolling Serato-style
    // view) is now the canonical animated waveform. The per-deck WS
    // container is kept ONLY to anchor the loop-region + hot-cue
    // overlays (which use percentage-based absolute positioning). We
    // make the WS visualization static + dim:
    //   - cursorWidth = 0  → no left-to-right playhead cursor
    //   - progressColor matches waveColor → no "colored fill" growing
    //     rightward as the track plays
    //   - waveColor opacity dropped so the static thumbnail doesn't
    //     compete visually with the scrolling MirroredWaveform.
    // R10 A bug: at narrower popout widths users were seeing the WS
    // progressColor + cursor advance and interpreted it as "waves are
    // moving right." Removing both makes only the MirroredWaveform
    // animate, eliminating the perceived direction reversal.
    wsARef.current = WaveSurfer.create({
      container: containerARef.current, height: 36,
      waveColor: "#FF007F33", progressColor: "#FF007F33", cursorColor: "transparent", cursorWidth: 0,
      barWidth: 2, barGap: 1, normalize: true, interact: true,
    });
    wsBRef.current = WaveSurfer.create({
      container: containerBRef.current, height: 36,
      waveColor: "#00F0FF33", progressColor: "#00F0FF33", cursorColor: "transparent", cursorWidth: 0,
      barWidth: 2, barGap: 1, normalize: true, interact: true,
    });
    wsARef.current.on("interaction", () => engineRef.current?.deckA.seekTo(wsARef.current.getCurrentTime()));
    wsBRef.current.on("interaction", () => engineRef.current?.deckB.seekTo(wsBRef.current.getCurrentTime()));
    return () => {
      try { wsARef.current?.destroy(); } catch {/**/}
      try { wsBRef.current?.destroy(); } catch {/**/}
      wsARef.current = null; wsBRef.current = null;
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const a = engineRef.current?.deckA, b = engineRef.current?.deckB;
      if (a?.duration && wsARef.current) wsARef.current.setTime(a.currentTime);
      if (b?.duration && wsBRef.current) wsBRef.current.setTime(b.currentTime);
      // Phase A R6 B3 — defensive React state sync for time labels.
      // The event-driven positionchange subscription has proven flaky in
      // production (popout BC race, React 18 batching). This interval
      // guarantees the deck cards' "0:00 / 4:14" labels and progress
      // overlays update at 5Hz regardless.
      if (a) setDeckA((s) => {
        const np = a.currentTime || 0, nd = a.duration || 0, npl = !!a.playing;
        if (s.pos === np && s.dur === nd && s.playing === npl) return s;
        return { ...s, pos: np, dur: nd, playing: npl };
      });
      if (b) setDeckB((s) => {
        const np = b.currentTime || 0, nd = b.duration || 0, npl = !!b.playing;
        if (s.pos === np && s.dur === nd && s.playing === npl) return s;
        return { ...s, pos: np, dur: nd, playing: npl };
      });
    }, 200);
    return () => clearInterval(id);
  }, []);

  // ResizeObserver: narrow stacking when the console container is < 900px wide.
  // Only active in inline layout (the fixed-bottom layout uses screen width naturally).
  useEffect(() => {
    if (layout !== "inline" || !outerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = e.contentRect.width;
        setNarrow(w < 900);
        setCompact(w < 640);
      }
    });
    ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [layout]);

  const broadcastNowPlaying = useCallback((deckLabel, title) => {
    onNowPlaying?.(deckLabel, title);
    if (!room) return;
    const payload = {
      role: "dj",
      display_name: room.localParticipant?.name,
      nowPlaying: `Deck ${deckLabel}: ${title}`,
    };
    // Queue if we're not Connected yet; the post-connect flush will pick it up.
    if (room.state !== ConnectionState.Connected) {
      pendingMetaRef.current = payload;
      return;
    }
    try {
      room.localParticipant.setMetadata(JSON.stringify(payload));
    } catch (e) {
      console.warn("setMetadata failed (non-fatal):", e);
    }
  }, [room, onNowPlaying]);

  // ---------------- Phase 2.1: Share Audio ----------------
  const setNowPlayingLabel = useCallback((label) => {
    if (!room) return;
    const payload = {
      role: "dj",
      display_name: room.localParticipant?.name,
      nowPlaying: label,
    };
    if (room.state !== ConnectionState.Connected) { pendingMetaRef.current = payload; return; }
    try { room.localParticipant.setMetadata(JSON.stringify(payload)); }
    catch (e) { console.warn("setMetadata failed (non-fatal):", e); }
  }, [room]);

  const stopStageIn = useCallback(async () => {
    // Tear down the captured stream + analyser, restore engine publication.
    if (stageInAnalyserRef.current) {
      try { stageInAnalyserRef.current.disconnect(); } catch { /* */ }
      stageInAnalyserRef.current = null;
    }
    if (stageInStopRef.current) {
      try { stageInStopRef.current(); } catch { /* */ }
      stageInStopRef.current = null;
    }
    if (room && stageInLkRef.current) {
      try { await room.localParticipant.unpublishTrack(stageInLkRef.current.mediaStreamTrack, true); }
      catch (e) { console.warn("unpublish stage-in:", e); }
      stageInLkRef.current = null;
    }
    // Re-publish engine master
    const eng = engineRef.current;
    if (room && eng) {
      try {
        const mst = eng.getBroadcastTrack();
        const lk = new LocalAudioTrack(mst);
        await room.localParticipant.publishTrack(lk, { name: "DJ Console Master", source: Track.Source.Microphone });
        musicLkRef.current = lk;
      } catch (e) { console.warn("re-publish master failed:", e); }
    }
    setStageInOn(false);
    setStageInLabel("");
    setStageInError("");
  }, [room]);

  const startStageIn = useCallback(async () => {
    setStageInError("");
    if (isMac() && isSafari()) { setShowSafariModal(true); return; }
    if (!isStageInSupported()) {
      setStageInError("Your browser doesn't expose system / app audio capture. Use Chrome or Edge on desktop.");
      return;
    }
    let cap;
    try { cap = await captureStageInAudio(); }
    catch (err) {
      if (err?.message === "NO_AUDIO_TRACK_SELECTED") {
        setStageInError("No audio in the source you picked. Re-share and tick \"Share tab audio\" or \"Share system audio\".");
      } else if (err?.name === "NotAllowedError") {
        setStageInError("Capture permission denied.");
      } else {
        setStageInError(`Capture failed: ${err?.message || err}`);
      }
      return;
    }
    const eng = engineRef.current;
    if (!eng) { cap.stop(); return; }

    // Pause both internal decks so DJ's monitor stops doubling.
    try { eng.deckA.pause(); eng.deckB.pause(); } catch { /* */ }

    // Unpublish current music track if any.
    if (room && musicLkRef.current) {
      try { await room.localParticipant.unpublishTrack(musicLkRef.current.mediaStreamTrack, true); }
      catch (e) { console.warn("unpublish music failed:", e); }
      musicLkRef.current = null;
    }

    // Publish Share Audio audio.
    if (room) {
      try {
        const lk = new LocalAudioTrack(cap.audioTrack);
        await room.localParticipant.publishTrack(lk, { name: "Share Audio", source: Track.Source.Microphone });
        stageInLkRef.current = lk;
      } catch (e) {
        console.warn("publish stage-in failed:", e);
        cap.stop();
        setStageInError(`Publish failed: ${e?.message || e}`);
        return;
      }
    }

    // Tap an analyser for the local VU.
    try {
      const src = eng.ctx.createMediaStreamSource(cap.stream);
      const an = eng.ctx.createAnalyser();
      an.fftSize = 256;
      src.connect(an); // NOT to destination — we don't monitor; DJ already hears via their own software
      stageInAnalyserRef.current = an;
    } catch (e) { /* analyser is non-critical */ }

    // Auto-cleanup if the OS-level "Stop sharing" is hit.
    cap.audioTrack.addEventListener("ended", () => { stopStageIn(); }, { once: true });

    stageInStopRef.current = cap.stop;
    setStageInOn(true);
    setStageInLabel(cap.label || "Captured audio");
    // Push current Now Playing override to listeners
    const label = (npOverride?.trim() || "Live mix (Share Audio)").slice(0, 80);
    setNowPlayingLabel(`Share Audio · ${label}`);
    onNowPlaying?.("Stage", label);
  }, [room, npOverride, onNowPlaying, setNowPlayingLabel, stopStageIn]);

  // Push override label changes to listeners (when active)
  useEffect(() => {
    if (!stageInOn) return;
    const label = (npOverride?.trim() || "Live mix (Share Audio)").slice(0, 80);
    setNowPlayingLabel(`Share Audio · ${label}`);
    try { localStorage.setItem("sr-dj-np-override", npOverride || ""); } catch { /* */ }
  }, [npOverride, stageInOn, setNowPlayingLabel]);

  // One-time Mac+Chrome tip
  useEffect(() => {
    if (!isMac() || isSafari()) return;
    try {
      const seen = localStorage.getItem("sr-mac-chrome-tip-seen");
      if (!seen) setShowMacTip(true);
    } catch { /* */ }
  }, []);
  const dismissMacTip = () => {
    setShowMacTip(false);
    try { localStorage.setItem("sr-mac-chrome-tip-seen", "1"); } catch { /* */ }
  };

  // ---------------- Phase 2.1: Recording ----------------
  const formatBytes = (b) => b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(1)} MB`;
  const formatMs = (ms) => {
    const s = Math.max(0, Math.round(ms/1000));
    const mm = Math.floor(s/60), ss = s % 60;
    return `${mm}:${String(ss).padStart(2,"0")}`;
  };

  const pickRecordStream = () => {
    // Always record the actual currently-broadcasting track. In Share Audio mode that's
    // the captured stream; otherwise it's the engine master.
    if (stageInOn && stageInLkRef.current?.mediaStreamTrack) {
      return new MediaStream([stageInLkRef.current.mediaStreamTrack]);
    }
    return engineRef.current?.broadcastDest?.stream;
  };

  const startRecording = useCallback(() => {
    if (recordingOn) return;
    const stream = pickRecordStream();
    if (!stream) { console.warn("No stream available to record"); return; }
    let mime = "audio/webm;codecs=opus";
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported(mime)) {
      mime = "audio/webm";
      if (!MediaRecorder.isTypeSupported(mime)) {
        console.warn("MediaRecorder webm unsupported on this browser");
        return;
      }
    }
    recChunksRef.current = [];
    const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 192000 });
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) recChunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(recChunksRef.current, { type: mime });
      recChunksRef.current = [];
      const durationMs = Date.now() - recStartedAtRef.current;
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 13).replace(/(\d{8})(\d{4})/, "$1-$2");
      const filename = `stackroom-${ts}.webm`;
      setRecResult({ blob, url, filename, bytes: blob.size, durationMs });
      setRecordingOn(false);
      setRecElapsed(0);
    };
    recStartedAtRef.current = Date.now();
    rec.start(1000);
    recorderRef.current = rec;
    setRecordingOn(true);
  }, [recordingOn, stageInOn]);

  const stopRecording = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch { /* */ }
    }
  }, []);

  const downloadRec = () => {
    if (!recResult) return;
    const a = document.createElement("a");
    a.href = recResult.url; a.download = recResult.filename;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const discardRec = () => {
    if (recResult?.url) { try { URL.revokeObjectURL(recResult.url); } catch {/**/} }
    setRecResult(null);
  };

  // Elapsed timer + beforeunload guard while recording.
  useEffect(() => {
    if (!recordingOn) return;
    const id = setInterval(() => setRecElapsed(Date.now() - recStartedAtRef.current), 500);
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => { clearInterval(id); window.removeEventListener("beforeunload", onBeforeUnload); };
  }, [recordingOn]);

  // ---------------- Phase 2.1: Ghost rows (IndexedDB cache) ----------------
  useEffect(() => {
    let cancelled = false;
    listGhosts().then(rows => { if (!cancelled) setGhosts(rows || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const refreshGhosts = async () => {
    try { setGhosts(await listGhosts()); } catch { /* */ }
  };

  const dismissGhost = async (hash) => {
    try { await removeGhost(hash); } catch { /* */ }
    refreshGhosts();
  };

  const reattachGhost = (hash) => {
    pendingGhostAttachRef.current = hash;
    ghostInputRef.current?.click();
  };

  const onGhostFilePicked = async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = "";
    if (!file) return;
    const wanted = pendingGhostAttachRef.current;
    pendingGhostAttachRef.current = null;
    try {
      const h = await hashFile(file);
      if (wanted && h !== wanted) {
        setStageInError("That file doesn't match the cached entry — it's a different track. Use 'Add tracks' to import it fresh.");
        setTimeout(() => setStageInError(""), 4000);
        return;
      }
      const cached = await getByHash(h);
      const id = crypto.randomUUID();
      const entry = { id, file, name: file.name, duration: cached?.duration || 0, bpm: cached?.bpm || 0, hash: h, fromGhost: true };
      setTracks(prev => [entry, ...prev]);
      // Promote ghost out of the ghost list — it now lives in the live strip
      setGhosts(prev => prev.filter(g => g.hash !== h));
    } catch (err) { console.warn("ghost re-attach failed:", err); }
  };


  const handleFiles = async (filesList) => {
    const files = Array.from(filesList).filter(f => f.type.startsWith("audio/"));
    // Optimistically add the rows first so the UI feels instant.
    const seeds = files.map(f => ({ id: crypto.randomUUID(), file: f, name: f.name, duration: 0, bpm: 0, hash: null }));
    setTracks(prev => [...prev, ...seeds]);
    // Hash + cache lookup in parallel.
    for (const seed of seeds) {
      hashFile(seed.file).then(async (hash) => {
        const cached = await getByHash(hash);
        setTracks(prev => prev.map(t => t.id === seed.id ? {
          ...t, hash,
          bpm: t.bpm || cached?.bpm || 0,
          duration: t.duration || cached?.duration || 0,
        } : t));
        // If the same file is in the ghost list, fold it out (it now lives in the live strip).
        setGhosts(prev => prev.filter(g => g.hash !== hash));
      }).catch(() => {});
    }
  };

  const loadToDeck = async (track, which) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : which === "B" ? eng.deckB : which === "C" ? eng.deckC : eng.deckD;
    if (!deck) return;
    const ws = which === "A" ? wsARef.current : which === "B" ? wsBRef.current : null;
    const setS = which === "A" ? setDeckA : which === "B" ? setDeckB : which === "C" ? setDeckC : setDeckD;
    try {
      const { duration, title, url } = await deck.loadFile(track.file);
      // If we have a cached BPM (from a prior session for the same file), seed the deck.
      let initialBpm = 0;
      if (track.hash) {
        const cached = await getByHash(track.hash);
        if (cached?.bpm) { initialBpm = cached.bpm; deck.bpm = cached.bpm; }
        if (cached?.hotCues && Array.isArray(cached.hotCues)) {
          deck.hotCues = cached.hotCues.slice(0, 4).concat([null,null,null,null]).slice(0, 4);
        }
      }
      setS(s => ({ ...s, title, dur: duration, pos: 0, bpm: initialBpm, hotCues:[...deck.hotCues], loop:{start:null,end:null,on:false} }));
      ws?.load(url);
      broadcastNowPlaying(which, title);
      // Persist (or refresh) the cache entry.
      if (track.hash) {
        saveTrack({ hash: track.hash, filename: track.file.name, duration, bpm: initialBpm, hotCues: deck.hotCues, lastLoadedTo: which }).catch(() => {});
      }
      // Detect/refresh BPM in the background.
      deck.detectBPM().then(bpm => {
        if (!bpm) return;
        setS(s => ({ ...s, bpm }));
        setTracks(p => p.map(t => t.id === track.id ? { ...t, bpm, duration } : t));
        if (track.hash) saveTrack({ hash: track.hash, bpm, hotCues: deck.hotCues }).catch(() => {});
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[DjConsole2] loadToDeck failed deck=${which} track=${track?.name} hasFile=${!!track?.file}:`, e);
      try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "error", msg: `Deck ${which} load failed: ${e?.message || e}` } })); } catch { /* */ }
    }
  };

  const loadFromLibrary = (track, which) => loadToDeck(track, which);

  const playPause = (which) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : eng.deckB;
    if (deck.playing) { deck.pause(); bcRef.current?.postMessage({ type: "pause", deck: which }); }
    else { deck.play(); bcRef.current?.postMessage({ type: "play", deck: which }); }
  };
  const cue = (which) => engineRef.current?.[which === "A" ? "deckA" : "deckB"].cue();
  const setXf = (v) => { setXfader(v); engineRef.current?.setCrossfader(v); bcRef.current?.postMessage({ type:"xfader", value:v }); };
  const setMv = (v) => { setMaster(v); engineRef.current?.setMasterGain(v); };
  // Phase A R10 B — master deck for BPM sync. Defaults to "A"; user can
  // re-master via the per-deck master toggle. Persisted in localStorage
  // so it survives popout reload.
  const [masterDeck, setMasterDeck] = useState(() => {
    try { return localStorage.getItem("sr-dj-master-deck") || "A"; } catch { return "A"; }
  });
  useEffect(() => {
    try { localStorage.setItem("sr-dj-master-deck", masterDeck); } catch { /* */ }
  }, [masterDeck]);
  // Phase A R12 — Auto-Sync on Load. When ON, any newly-loaded track on a
  // non-master deck auto-runs the same engine sync path as clicking Sync.
  // Pitch adjusts to match master's effective BPM (clamp ±8%). Silent
  // skip if no master or master has no BPM yet. Default ON.
  const [autoSyncOnLoad, setAutoSyncOnLoad] = useState(() => {
    try { return localStorage.getItem("sr-auto-sync-on-load") !== "0"; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("sr-auto-sync-on-load", autoSyncOnLoad ? "1" : "0"); } catch { /* */ }
  }, [autoSyncOnLoad]);
  // Refs so the long-lived bpm-event listeners installed once at mount
  // always read the LATEST master + auto-sync values (closures captured
  // at mount would otherwise see stale state).
  const masterDeckRef = useRef(masterDeck);
  const autoSyncOnLoadRef = useRef(autoSyncOnLoad);
  useEffect(() => { masterDeckRef.current = masterDeck; }, [masterDeck]);
  useEffect(() => { autoSyncOnLoadRef.current = autoSyncOnLoad; }, [autoSyncOnLoad]);
  // Phase A R12 — auto-sync helper. Mirrors the manual Sync button's
  // engine path: target.sync(masterLiveBPM) where masterLiveBPM is the
  // master's effectiveBpm (bpm × pitch). Silent-skips when toggle is
  // off, when the target IS the master, or when either BPM is missing.
  const tryAutoSync = useCallback((which) => {
    if (!autoSyncOnLoadRef.current) return;
    const masterD = masterDeckRef.current;
    if (which === masterD) return; // master never auto-syncs to anything
    const eng = engineRef.current;
    if (!eng) return;
    const target = which === "A" ? eng.deckA : which === "B" ? eng.deckB : null;
    const master = masterD === "A" ? eng.deckA : masterD === "B" ? eng.deckB : null;
    if (!target || !master) return;
    const masterLiveBPM = master.effectiveBpm || master.bpm || 0;
    const targetDetectedBPM = target.bpm || 0;
    if (!masterLiveBPM || !targetDetectedBPM) return; // silent skip
    const ratio = masterLiveBPM / targetDetectedBPM;
    const pct = Math.max(-8, Math.min(8, (ratio - 1) * 100));
    // eslint-disable-next-line no-console
    console.log(`[auto-sync] deck=${which} masterLiveBPM=${masterLiveBPM.toFixed(2)} targetDetectedBPM=${targetDetectedBPM.toFixed(2)} → pitch=${pct.toFixed(1)}%`);
    try { target.sync(masterLiveBPM); } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[auto-sync] target.sync threw for deck ${which}:`, err);
    }
  }, []);
  const sync = (which) => {
    const eng = engineRef.current; if (!eng) return;
    // Phase A R10 B — sync to MASTER deck's LIVE BPM (effectiveBpm = bpm
    // × (1 + pitch/100)), not the static detected BPM. Pre-R10 the
    // handler called `deck.sync(otherDeck.bpm)` which ignored the
    // master's own pitch and (in the popout) ignored the argument
    // entirely since RemoteDeck.sync() took no parameter.
    const targetDeck = which === "A" ? eng.deckA : eng.deckB;
    const master     = (masterDeck === "A") ? eng.deckA : eng.deckB;
    if (which === masterDeck) {
      // eslint-disable-next-line no-console
      console.log(`[sync] no-op: deck ${which} IS the master`);
      return;
    }
    const masterLiveBPM = master?.effectiveBpm || master?.bpm || 0;
    if (!masterLiveBPM || !targetDeck?.bpm) {
      // eslint-disable-next-line no-console
      console.warn(`[sync] missing BPM: masterLive=${masterLiveBPM} target.bpm=${targetDeck?.bpm}`);
      try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "warning", msg: `Sync skipped — load tracks on both decks first` } })); } catch { /* */ }
      return;
    }
    const ratio = masterLiveBPM / targetDeck.bpm;
    const computedPitch = Math.max(-8, Math.min(8, (ratio - 1) * 100));
    // eslint-disable-next-line no-console
    console.log(`[sync] target=${which} masterLiveBPM=${masterLiveBPM.toFixed(2)} targetDetectedBPM=${targetDeck.bpm.toFixed(2)} → computedPitch=${computedPitch.toFixed(2)}%`);
    targetDeck.sync(masterLiveBPM);
  };
  // Phase 3.6 R2 — Match BPM input. Type a target BPM, lock pitch to it.
  // Falls back gracefully on RemoteEngine which forwards via BroadcastChannel.
  const matchBpm = (which, targetBpm) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : eng.deckB;
    if (typeof deck.matchBPM === "function") deck.matchBPM(targetBpm);
    // Optimistic state — engine "pitchchange" event will reconcile.
    const setS = which === "A" ? setDeckA : setDeckB;
    if (deck.bpm && targetBpm) {
      const ratio = Math.max(0.92, Math.min(1.08, targetBpm / deck.bpm));
      const p = (ratio - 1) * 100;
      setS((s) => ({ ...s, pitch: p }));
    }
  };
  // Phase 3.6 R2 — EQ kill switches (Low/Mid/High). Toggling kills the band
  // (drops to -∞ dB via -24dB cut). Restore returns to the previous knob value.
  const killEq = (which, band) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : eng.deckB;
    const setS = which === "A" ? setDeckA : setDeckB;
    const kills = deck._kills || { low: null, mid: null, high: null };
    if (kills[band] != null) {
      // Restore
      if (band === "low")  deck.setLow(kills.low);
      if (band === "mid")  deck.setMid(kills.mid);
      if (band === "high") deck.setHigh(kills.high);
      kills[band] = null;
    } else {
      // Kill — remember the current band gain so we can restore.
      const cur = band === "low" ? (deck.low?.gain?.value || 0)
              :  band === "mid" ? (deck.mid?.gain?.value || 0)
              :                    (deck.high?.gain?.value || 0);
      kills[band] = cur;
      if (band === "low")  deck.setLow(-1);
      if (band === "mid")  deck.setMid(-1);
      if (band === "high") deck.setHigh(-1);
    }
    deck._kills = kills;
    setS((s) => ({ ...s, kills: { ...kills } }));
  };
  // Phase 3.6 R2 — Slip mode toggle per deck.
  const toggleSlip = (which) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : which === "B" ? eng.deckB : which === "C" ? eng.deckC : eng.deckD;
    if (!deck) return;
    const next = !(deck.slipActive ?? deck.slip);
    if (typeof deck.setSlip === "function") deck.setSlip(next);
    else deck.slip = next;
    const setS = which === "A" ? setDeckA : which === "B" ? setDeckB : which === "C" ? setDeckC : setDeckD;
    setS && setS((s) => ({ ...s, slip: next }));
  };

  // Phase A R2 — Loop rolls (press-and-hold beat divisions).
  const loopRoll = (which, beats, momentary = true) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : which === "B" ? eng.deckB : which === "C" ? eng.deckC : eng.deckD;
    if (!deck) return;
    if (typeof deck.startLoop === "function") deck.startLoop(beats, momentary);
  };
  const loopRollRelease = (which) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : which === "B" ? eng.deckB : which === "C" ? eng.deckC : eng.deckD;
    if (!deck) return;
    if (typeof deck.releaseLoop === "function") deck.releaseLoop();
  };
  // Phase A R2 — Tap-tempo fallback (4× taps to estimate BPM)
  const tapTempo = (which) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : which === "B" ? eng.deckB : which === "C" ? eng.deckC : eng.deckD;
    if (!deck) return;
    if (typeof deck.tapTempo === "function") {
      const bpm = deck.tapTempo();
      const setS = which === "A" ? setDeckA : which === "B" ? setDeckB : which === "C" ? setDeckC : setDeckD;
      setS && setS((s) => ({ ...s, bpm: bpm || s.bpm }));
    }
  };
  // FX rack handlers — proxy to engine.fxRack (local DjEngine or RemoteFxRack).
  const onFxEnabled = (name, enabled) => {
    const eng = engineRef.current; if (!eng?.fxRack) return;
    eng.fxRack.setEnabled(name, enabled);
    setFxState((s) => ({ ...s, [name]: { ...(s[name] || {}), enabled } }));
  };
  const onFxParam = (name, param, value) => {
    const eng = engineRef.current; if (!eng?.fxRack) return;
    eng.fxRack.setParam(name, param, value);
    setFxState((s) => ({ ...s, [name]: { ...(s[name] || {}), [param === "wet" ? "wetLevel" : param]: value } }));
  };
  const onFxSend = (name, source) => {
    const eng = engineRef.current; if (!eng?.fxRack) return;
    eng.fxRack.setSendFrom(name, source);
    setFxState((s) => ({ ...s, [name]: { ...(s[name] || {}), sendFrom: source } }));
  };
  // Deck xfader assign (used by Pro 4-Deck)
  const setDeckAssign = (which, assign) => {
    const eng = engineRef.current; if (!eng?.setDeckXfaderAssign) return;
    eng.setDeckXfaderAssign(which, assign);
    setXfaderAssigns((s) => ({ ...s, [which]: assign }));
  };

  // Hot cues
  const hotCue = (which, slot, modifier) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : eng.deckB;
    const setS = which === "A" ? setDeckA : setDeckB;
    if (modifier === "clear") { deck.clearHotCue(slot); }
    else if (deck.hotCues[slot] == null) { deck.setHotCue(slot); }
    else { deck.jumpHotCue(slot); }
    setS(s => ({ ...s, hotCues: [...deck.hotCues] }));
  };

  // Loop
  const loopIn = (which) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : eng.deckB;
    const setS = which === "A" ? setDeckA : setDeckB;
    deck.loop.start = deck.currentTime;
    setS(s => ({ ...s, loop: { ...s.loop, start: deck.loop.start } }));
  };
  const loopOut = (which) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : eng.deckB;
    const setS = which === "A" ? setDeckA : setDeckB;
    if (deck.loop.start == null || deck.currentTime <= deck.loop.start) return;
    deck.loop.end = deck.currentTime;
    setS(s => ({ ...s, loop: { ...s.loop, end: deck.loop.end } }));
  };
  const loopToggle = (which) => {
    const eng = engineRef.current; if (!eng) return;
    const deck = which === "A" ? eng.deckA : eng.deckB;
    const setS = which === "A" ? setDeckA : setDeckB;
    const on = deck.toggleLoop();
    setS(s => ({ ...s, loop: { ...s.loop, on } }));
  };

  const popOut = () => {
    // Phase A R2 — open the dedicated DjBoardPopout route. The popup window
    // loads <DjBoardPopout> which renders <DjConsole2 mode="popout" /> and
    // talks to this main tab over BroadcastChannel — full UI parity, and
    // closing the popup does NOT touch LiveKit (engine lives here).
    const rid = (typeof room === "string" ? room : room?.room_id) ||
                (window.location.pathname.match(/\/r\/([^/]+)/)?.[1]) || "";
    if (!rid) return;
    window.open(`/r/${rid}/dj-board`, `stackroom_dj_board_${rid}`,
      "popup=yes,width=1280,height=780,toolbar=no,menubar=no,location=no");
  };

  // Drop zone handlers (whole strip)
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files); };

  if (mini) {
    return (
      <div className="fixed inset-0 bg-[#050508] text-white p-3 flex flex-col gap-2">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Mini DJ</div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {["A","B"].map((D) => {
            const s = D === "A" ? deckA : deckB;
            return (
              <div key={D} className="bg-white/5 rounded-md p-2">
                <div className="text-[10px] text-white/40">Deck {D}</div>
                <div className="font-semibold truncate">{s.title}</div>
                <div className="flex gap-1 mt-1">
                  <button onClick={() => playPause(D)} className="px-2 py-0.5 bg-white/10 rounded">{s.playing ? "⏸" : "▶"}</button>
                  <button onClick={() => cue(D)} className="px-2 py-0.5 bg-white/10 rounded">CUE</button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-auto">
          <input type="range" min={-1} max={1} step={0.01} value={xfader}
            onChange={(e) => setXf(parseFloat(e.target.value))} className="sr-xfader w-full" />
        </div>
      </div>
    );
  }

  const aLive = deckA.playing && xfader < 0.85;
  const bLive = deckB.playing && xfader > -0.85;

  const isInline = layout === "inline";
  // In fixed mode: pin to bottom of screen. In inline mode: fill parent container.
  const outerClass = isInline
    ? "h-full w-full overflow-auto px-2 py-2"
    : "fixed bottom-0 inset-x-0 z-30 px-2 pb-2";
  const innerClass = isInline
    ? "sr-glass rounded-2xl p-3"
    : "max-w-[1600px] mx-auto sr-glass rounded-2xl p-3 shadow-[0_-12px_50px_-12px_rgba(0,0,0,0.7)]";

  const gridCols = narrow ? "grid-cols-1" : "grid-cols-12";
  const deckSpan = narrow ? "col-span-1" : "lg:col-span-5 col-span-12";
  const mixerSpan = narrow ? "col-span-1" : "lg:col-span-2 col-span-12";

  return (
    <div ref={outerRef} className={`${outerClass} sr-skin-root`} data-testid="dj-console-v2"
         data-sr-tier={tier} data-sr-layout={activeLayout}>
      {/* Hidden ghost re-attach file input */}
      <input ref={ghostInputRef} type="file" accept="audio/*" className="hidden"
        onChange={onGhostFilePicked} data-testid="ghost-file-input" />

      <div className={innerClass}>
        {/* Phase A R3 — Tier picker (replaces skin + layout). One control. */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <label className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold dj-readable-select">
            <LayoutIcon className="w-3 h-3" />
            <select value={tier} onChange={(e) => setTier(parseInt(e.target.value, 10))}
              className="bg-white/5 border border-white/10 rounded px-1.5 py-1 text-[10px] uppercase tracking-[0.12em] text-white"
              data-testid="dj-tier-picker">
              {TIERS.map((t) => (
                <option key={t.key} value={t.key} data-testid={`dj-tier-${t.key}`}>{t.label}</option>
              ))}
            </select>
          </label>
          {/* Phase A R3 — Library toggle */}
          <button onClick={() => setLibraryOpen((v) => !v)}
                  className={`px-2 py-1 rounded text-[10px] uppercase tracking-[0.12em] font-bold ${libraryOpen ? "bg-emerald-500/25 text-emerald-200 border border-emerald-500/40" : "bg-white/5 text-white/70 border border-white/10 hover:text-white"}`}
                  data-testid="dj-library-toggle">
            {libraryOpen ? "Library ▾" : "Library"}
          </button>
          {/* Phase A R6 B1 — Mix Now relocated to AutoDjPanel. Removed from toolbar. */}
          {/* Phase A R3 / R9 D — Record Mix is the SINGLE recorder. The
              R3-era `dj-record-btn` duplicate was deleted in R9 D because
              users couldn't tell which one was active and it duplicated
              the broadcastDest stream capture. Record Mix uses the
              `useRecorder` hook with a 5s timeslice for safe 2-hour sets. */}
          {!recorder.recording && !recorder.lastBlob && (
            <button onClick={recorder.start}
                    disabled={!recorder.mimeSupported}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-[0.12em] bg-white/5 hover:bg-rose-500/20 text-white/80 hover:text-rose-200 border border-white/10"
                    title={recorder.mimeSupported ? "Start recording the master output (downloadable on stop)" : "MediaRecorder not supported"}
                    data-testid="dj-record-mix">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF0033] inline-block" /> Record Mix
            </button>
          )}
          {recorder.recording && (
            <button onClick={recorder.stop}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-[0.12em] bg-[#FF0033]/30 border border-[#FF0033] text-[#FF8FA8] animate-pulse"
                    title="Stop recording — file will be ready to download"
                    data-testid="dj-record-stop">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF0033] inline-block animate-ping" />
              REC · <span data-testid="dj-record-timer">{String(Math.floor(recorder.elapsed/3600)).padStart(2,"0")}:{String(Math.floor(recorder.elapsed/60)%60).padStart(2,"0")}:{String(recorder.elapsed%60).padStart(2,"0")}</span>
            </button>
          )}
          {!recorder.recording && recorder.lastBlob && (
            <span className="inline-flex items-center gap-1 text-[10px]">
              <button onClick={() => { recorder.download(); try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "success", msg: `Mix downloaded · ${(recorder.lastBlob.size / 1024 / 1024).toFixed(1)} MB` } })); } catch { /* */ } }}
                      className="px-2 py-1 rounded bg-emerald-500/25 text-emerald-100 border border-emerald-500/40 font-bold uppercase tracking-[0.12em]"
                      data-testid="dj-record-download">
                ↓ Save mix
              </button>
              <button onClick={async () => {
                        const rid = (typeof room === "string" ? room : room?.room_id) ||
                                    (window.location.pathname.match(/\/r\/([^/]+)/)?.[1]) || "";
                        if (!rid) return;
                        try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "info", msg: "Uploading to room…" } })); } catch { /* */ }
                        const result = await recorder.uploadToRoom(rid);
                        if (result) { try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "success", msg: `Saved to room · ${(result.size / 1024 / 1024).toFixed(1)} MB` } })); } catch { /* */ } }
                        else { try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "error", msg: `Upload failed: ${recorder.error || "unknown"}` } })); } catch { /* */ } }
                      }}
                      className="px-2 py-1 rounded bg-white/5 text-white/70 border border-white/10 hover:text-white font-bold uppercase tracking-[0.12em]"
                      data-testid="dj-record-save-to-room">
                ↑ Save to room
              </button>
            </span>
          )}
          <span className="text-white/15 mx-0.5" aria-hidden>·</span>
          <div className="inline-flex rounded-md border border-white/10 overflow-hidden" data-testid="source-mode-toggle">
            <button
              onClick={() => { if (stageInOn) stopStageIn(); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] transition-colors ${
                !stageInOn ? "bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F]" : "bg-white/5 text-white/60 hover:text-white"
              }`}
              data-testid="source-decks-btn"
              title="Use the built-in dual-deck engine">
              <Disc3 className="w-3.5 h-3.5" /> Full Spectrum Decks
            </button>
            <button
              onClick={() => {
                if (stageInOn) return;
                if (stageInOnMobile) { setShowMobileBlockModal(true); return; }
                startStageIn();
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] transition-colors ${
                stageInOn ? "bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F]" : stageInOnMobile ? "bg-white/5 text-white/30 cursor-help" : "bg-white/5 text-white/60 hover:text-white"
              }`}
              data-testid="source-stagein-btn"
              title={stageInOnMobile ? "Share Audio is unavailable on mobile browsers — tap for details" : "Capture system / app audio from your DJ software"}>
              <Radio className="w-3.5 h-3.5" /> Share Audio{stageInOnMobile && <span className="ml-1 text-[9px] opacity-70">desktop only</span>}
            </button>
          </div>

          <div className="flex-1" />

          <button onClick={popOut}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-xs shrink-0"
            data-testid="dj-popout-btn">
            <Maximize2 className="w-3.5 h-3.5" /> Pop out
          </button>
        </div>

        {/* Share Audio banner */}
        {stageInOn && (
          <div className="mb-2 p-2 rounded-lg border border-[#FF007F]/40 bg-gradient-to-r from-[#FF007F]/15 via-transparent to-[#00F0FF]/15 shadow-[0_0_24px_-4px_rgba(255,0,127,0.4)] flex flex-wrap items-center gap-2"
            data-testid="stagein-banner">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#FF007F]/30 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
              <Radio className="w-3 h-3" /> Share Audio · LIVE
            </span>
            <span className="text-xs text-white/70 truncate max-w-[260px]" data-testid="stagein-source-label">{stageInLabel}</span>
            <label className="ml-auto flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/50">
              Now Playing
              <input
                type="text"
                value={npOverride}
                onChange={(e) => setNpOverride(e.target.value.slice(0, 80))}
                placeholder="Type what's playing (max 80 chars)"
                maxLength={80}
                className="bg-black/30 border border-white/10 focus:border-[#00F0FF] focus:outline-none rounded px-2 py-1 text-xs text-white w-[260px]"
                data-testid="stagein-nowplaying-input"
              />
            </label>
            <button
              onClick={stopStageIn}
              className="px-2.5 py-1 rounded bg-white/10 hover:bg-white/20 border border-white/10 text-[10px] font-bold uppercase tracking-[0.2em]"
              data-testid="stagein-stop-btn">
              End Share Audio
            </button>
          </div>
        )}

        {/* Inline error / warning toast */}
        {stageInError && (
          <div className="mb-2 p-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200 flex items-start gap-2"
            data-testid="stagein-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="flex-1">{stageInError}</span>
            <button onClick={() => setStageInError("")} className="opacity-60 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {/* Ghost rows: previously-loaded tracks from IndexedDB */}
        {ghosts.length > 0 && (
          <div className="mb-2 p-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02]" data-testid="ghost-strip">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[9px] uppercase tracking-[0.25em] text-white/40">Last session</span>
              <span className="text-[10px] text-white/30">re-attach the file to restore BPM + hot cues</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ghosts.slice(0, 8).map(g => (
                <span key={g.hash} className="inline-flex items-center gap-1 px-2 py-1 bg-white/5 rounded text-[11px]" data-testid={`ghost-row-${g.hash.slice(0,8)}`}>
                  <Music2 className="w-3 h-3 text-white/30" />
                  <span className="truncate max-w-[160px] text-white/60">{g.filename}</span>
                  {g.bpm ? <span className="text-white/30 font-mono text-[10px]">{g.bpm}bpm</span> : null}
                  <button
                    onClick={() => reattachGhost(g.hash)}
                    className="ml-1 px-1.5 py-0.5 bg-[#00F0FF]/20 hover:bg-[#00F0FF]/40 rounded text-[10px] font-bold"
                    data-testid={`ghost-reattach-${g.hash.slice(0,8)}`}>
                    Re-attach
                  </button>
                  <button onClick={() => dismissGhost(g.hash)} className="text-white/30 hover:text-white/70" title="Forget">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Track strip with drop zone */}
        <div
          className={`sr-drop rounded-lg p-2 mb-3 flex items-center gap-2 ${dragOver ? "is-over" : ""}`}
          onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
          data-testid="dj-track-strip">
          <input ref={fileInputRef} type="file" accept="audio/*" multiple className="hidden"
            onChange={(e) => handleFiles(e.target.files)} data-testid="dj-file-input" />
          <button onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-xs font-semibold shrink-0"
            data-testid="dj-add-tracks-btn">
            <Upload className="w-3.5 h-3.5" /> Add tracks
          </button>
          <div className="flex-1 overflow-x-auto whitespace-nowrap text-xs" onClick={(e) => { if (e.target === e.currentTarget && tracks.length === 0) fileInputRef.current?.click(); }}>
            {tracks.length === 0 ? (
              <span className="text-white/30">{dragOver ? "Drop tracks to import" : "Drop audio files here, or click 'Add tracks'."}</span>
            ) : tracks.map(t => (
              <span key={t.id} className="inline-flex items-center gap-1 mr-2 px-2 py-1 bg-white/5 rounded">
                <Music2 className="w-3 h-3 text-[#00F0FF]" />
                <span className="truncate max-w-[180px] text-white/80">{t.name}</span>
                {t.bpm ? <span className="text-white/40 font-mono text-[10px]">{t.bpm}bpm</span> : null}
                <button onClick={() => loadToDeck(t, "A")} className="px-1.5 bg-[#FF007F]/30 hover:bg-[#FF007F]/70 rounded text-[10px] font-bold" data-testid={`load-A-${t.id}`}>→A</button>
                <button onClick={() => loadToDeck(t, "B")} className="px-1.5 bg-[#00F0FF]/30 hover:bg-[#00F0FF]/70 rounded text-[10px] font-bold" data-testid={`load-B-${t.id}`}>→B</button>
                {activeLayout === "pro4" && (
                  <>
                    <button onClick={() => loadToDeck(t, "C")} className="px-1.5 bg-[#FF8A3D]/30 hover:bg-[#FF8A3D]/70 rounded text-[10px] font-bold" data-testid={`load-C-${t.id}`}>→C</button>
                    <button onClick={() => loadToDeck(t, "D")} className="px-1.5 bg-[#5FB3FF]/30 hover:bg-[#5FB3FF]/70 rounded text-[10px] font-bold" data-testid={`load-D-${t.id}`}>→D</button>
                  </>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Decks grid — dimmed but visible while Share Audio is active.
         * Phase 3.5 responsive layout:
         *   - >900px wide: classic 3-column DeckA · Mixer · DeckB
         *   - 640-900px:   stacked DeckA → DeckB → Mixer (mixer becomes a bar below)
         *   - ≤640px:      tab switcher [Deck A] [Deck B] [Mixer] [Library]
         */}
        {/* Phase A R11 D — at <sm (640px) the layout stacks vertically.
            Tier picker is forced to Tier 1 (Basic 2-Deck) and disabled.
            We also render the scrolling waveform at top instead of hiding
            it (pre-R11 compact hid the waveform entirely + showed tab
            switcher). */}
        {compact && (
          <div className="mb-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[10px] uppercase tracking-[0.18em] text-amber-200 font-bold"
               data-testid="dj-mobile-tier-hint">
            Basic 2-Deck · more tiers available on tablet+
          </div>
        )}
        {/* Phase A R4 A3 — VDJ-style mirrored-stacked waveforms.
            Deck A on top, Deck B mirrored below, full-height downbeat bars,
            BPM + Camelot key labels at the start of each waveform.
            In Pro 4-Deck layout we render a second pair (C+D) below. */}
        <div className="mb-3 space-y-2" data-testid="dj-mirrored-waveforms-wrap">
            <MirroredWaveform engineRef={engineRef} deckA={deckA} deckB={deckB}
                              onSeek={(label, frac) => {
                                const eng = engineRef.current; if (!eng) return;
                                const deck = label === "A" ? eng.deckA : eng.deckB;
                                const dur = deck?.duration || 0;
                                if (dur > 0) deck.seekTo(frac * dur);
                              }} />
            {!compact && activeLayout === "pro4" && (
              <MirroredWaveform engineRef={engineRef} deckA={deckC} deckB={deckD}
                                testid="mirrored-waveform-cd"
                                onSeek={(label, frac) => {
                                  const eng = engineRef.current; if (!eng) return;
                                  const deck = label === "A" ? eng.deckC : eng.deckD;
                                  const dur = deck?.duration || 0;
                                  if (dur > 0) deck.seekTo(frac * dur);
                                }} />
            )}
        </div>
        <div className={`grid ${gridCols} gap-3 transition-opacity ${stageInOn ? "opacity-40 pointer-events-none" : ""} ${compact ? "sr-dj-compact" : narrow ? "sr-dj-narrow" : ""}`}>
          {/* Phase A R2 — Performance layout: single tall pad grid (8×4 cues + loop rolls) */}
          {!compact && activeLayout === "perf" && (
            <PerformanceLayout
              deckA={deckA} deckB={deckB}
              engineRef={engineRef}
              onHotCue={(d, slot, mod) => hotCue(d, slot, mod)}
              onLoopRoll={(d, beats, momentary) => loopRoll(d, beats, momentary)}
              onLoopRollRelease={(d) => loopRollRelease(d)}
              onPlayPause={(d) => playPause(d)}
              onClearAllCues={(d) => {
                for (let i = 0; i < 8; i++) hotCue(d, i, "clear");
              }}
              onLoopExit={(d) => loopRollRelease(d)}
            />
          )}
          {/* Classic 2-deck / Pro 4-deck render decks A and B */}
          {activeLayout !== "perf" && (
            <DeckPanel which="A" state={deckA} otherState={deckB} containerRef={containerARef} engineRef={engineRef}
              spanClass={activeLayout === "pro4" ? (narrow ? "col-span-1" : "col-span-6") : deckSpan}
              live={aLive} cue={cueA} setCue={setCueA}
              isMaster={masterDeck === "A"} onSetMaster={() => setMasterDeck("A")}
              onPlayPause={() => playPause("A")} onCueBtn={() => cue("A")} onSync={() => sync("A")} onMatchBpm={(t) => matchBpm("A", t)}
              onHotCue={(slot, mod) => hotCue("A", slot, mod)}
              onLoopIn={() => loopIn("A")} onLoopOut={() => loopOut("A")} onLoopToggle={() => loopToggle("A")}
              onLoopRoll={(beats, momentary) => loopRoll("A", beats, momentary)}
              onLoopRollRelease={() => loopRollRelease("A")}
              onToggleSlip={() => toggleSlip("A")}
              onKillEq={(band) => killEq("A", band)}
              onTapTempo={() => tapTempo("A")} />
          )}
          {(activeLayout !== "perf") && (
            <DeckPanel which="B" state={deckB} otherState={deckA} containerRef={containerBRef} engineRef={engineRef}
              spanClass={activeLayout === "pro4" ? (narrow ? "col-span-1" : "col-span-6") : deckSpan}
              live={bLive} cue={cueB} setCue={setCueB} mirror
              isMaster={masterDeck === "B"} onSetMaster={() => setMasterDeck("B")}
              onPlayPause={() => playPause("B")} onCueBtn={() => cue("B")} onSync={() => sync("B")} onMatchBpm={(t) => matchBpm("B", t)}
              onHotCue={(slot, mod) => hotCue("B", slot, mod)}
              onLoopIn={() => loopIn("B")} onLoopOut={() => loopOut("B")} onLoopToggle={() => loopToggle("B")}
              onLoopRoll={(beats, momentary) => loopRoll("B", beats, momentary)}
              onLoopRollRelease={() => loopRollRelease("B")}
              onToggleSlip={() => toggleSlip("B")}
              onKillEq={(band) => killEq("B", band)}
              onTapTempo={() => tapTempo("B")} />
          )}
          {/* Phase A R2 — Pro 4-Deck layout: decks C + D on second row */}
          {activeLayout === "pro4" && !compact && (
            <DeckPanel which="C" state={deckC} containerRef={null} engineRef={engineRef}
              spanClass={narrow ? "col-span-1" : "col-span-6"}
              live={false} cue={false} setCue={() => {}}
              onPlayPause={() => playPause("C")} onCueBtn={() => cue("C")} onSync={() => sync("C")} onMatchBpm={(t) => matchBpm("C", t)}
              onHotCue={(slot, mod) => hotCue("C", slot, mod)}
              onLoopIn={() => loopIn("C")} onLoopOut={() => loopOut("C")} onLoopToggle={() => loopToggle("C")}
              onLoopRoll={(beats, momentary) => loopRoll("C", beats, momentary)}
              onLoopRollRelease={() => loopRollRelease("C")}
              onToggleSlip={() => toggleSlip("C")}
              onKillEq={(band) => killEq("C", band)}
              onTapTempo={() => tapTempo("C")} />
          )}
          {activeLayout === "pro4" && !compact && (
            <DeckPanel which="D" state={deckD} containerRef={null} engineRef={engineRef}
              spanClass={narrow ? "col-span-1" : "col-span-6"}
              live={false} cue={false} setCue={() => {}} mirror
              onPlayPause={() => playPause("D")} onCueBtn={() => cue("D")} onSync={() => sync("D")} onMatchBpm={(t) => matchBpm("D", t)}
              onHotCue={(slot, mod) => hotCue("D", slot, mod)}
              onLoopIn={() => loopIn("D")} onLoopOut={() => loopOut("D")} onLoopToggle={() => loopToggle("D")}
              onLoopRoll={(beats, momentary) => loopRoll("D", beats, momentary)}
              onLoopRollRelease={() => loopRollRelease("D")}
              onToggleSlip={() => toggleSlip("D")}
              onKillEq={(band) => killEq("D", band)}
              onTapTempo={() => tapTempo("D")} />
          )}
          {activeLayout !== "perf" && (
            <MixerPanel
              spanClass={activeLayout === "pro4" ? "col-span-12 lg:col-span-12 order-last" : mixerSpan}
              xfader={xfader} setXfader={setXf} master={master} setMaster={setMv} vu={vu}
              showAssigns={activeLayout === "pro4"}
              assigns={xfaderAssigns}
              onAssignChange={setDeckAssign}
              autoSyncOnLoad={autoSyncOnLoad}
              setAutoSyncOnLoad={setAutoSyncOnLoad}
            />
          )}
          {/* Phase A R11 D — the compact stub library card is removed —
              the full Auto-DJ + Library blocks below render inline now. */}
        </div>

        {/* Phase A R3 — FX rack + Auto-DJ row (FX only at tier ≥ 2).
            Phase A R11 D — rendered at compact too so mobile DJs have
            full Auto-DJ control in the vertical stack. FX rack stays
            hidden at compact (single column would push it too far down). */}
        <div className={`mt-3 grid ${compact ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-[1fr_320px]"} gap-3`} data-testid="dj-fx-autodj-row">
          {compact ? null : hasFx ? (
            <FxRack
              fxState={fxState}
              layoutMode={activeLayout}
              onSetEnabled={onFxEnabled}
              onSetParam={onFxParam}
              onSetSendFrom={onFxSend}
            />
          ) : (
            <div className="rounded-xl border border-white/10 bg-[var(--sr-surface)] p-3 text-[11px] text-white/40"
                 data-testid="dj-fx-locked">
              FX rack unlocks at Tier 2. Switch from the <strong>tier picker</strong> above.
            </div>
          )}
            <div className="rounded-xl border border-white/10 bg-[var(--sr-surface)] p-2.5">
              <AutoDjPanel
                engineRef={engineRef}
                playlist={autoDjPlaylist}
                setPlaylist={updatePlaylist}
                onLoadNext={async (t, target) => {
                  // Phase A R8.2 — when running in popout (engine is
                  // RemoteEngine), ask the MAIN tab to do the load via
                  // its canonical playlist + File (R8.1). This avoids
                  // re-shipping a File the popout no longer has after
                  // the BC roundtrip.
                  const eng = engineRef.current;
                  // eslint-disable-next-line no-console
                  console.log(`[autodj] onLoadNext fired track=${t?.name} trackId=${t?.trackId} target=${target} isPopout=${isPopout} hasFile=${!!t?.file} hasFileFlag=${!!t?.hasFile}`);
                  if (isPopout && eng?.loadPlaylistTrackOntoDeck) {
                    try {
                      const meta = await eng.loadPlaylistTrackOntoDeck(t.trackId, target);
                      // eslint-disable-next-line no-console
                      console.log(`[autodj] onLoadNext main-tab load OK trackId=${t.trackId} duration=${meta?.duration} bpm=${meta?.bpm}`);
                      return meta;
                    } catch (err) {
                      // eslint-disable-next-line no-console
                      console.error(`[autodj] onLoadNext main-tab load FAILED trackId=${t.trackId}:`, err);
                      try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "error", msg: `Auto-DJ load failed: ${err?.message || err}` } })); } catch { /* */ }
                      throw err;
                    }
                  }
                  // Inline path — main tab DjConsole2 owns the engine directly.
                  const meta = await loadToDeck(t, target);
                  const deck = target === "A" ? eng.deckA : eng.deckB;
                  return { duration: deck?.duration || 0, bpm: deck?.bpm || 0, key: deck?.detectedKey };
                }}
                onMixNow={() => {
                  // Phase A R6 B1 — Mix Now lives inside AutoDjPanel now.
                  const eng = engineRef.current;
                  if (!eng) return "no-op";
                  const res = eng.mixNow?.(8);
                  if (res === "no-op") {
                    try { window.alert?.("Load a track on the other deck or start Auto-DJ."); } catch {}
                  }
                  return res;
                }}
                onReattach={async (t) => {
                  // Phase A R5 follow-up R5F-5 — Re-attach a Library-sourced
                  // track. Permissions for a saved FSA folder handle must be
                  // re-granted on each page load via a user-gesture. The
                  // library hook handles that flow.
                  if (!t.sourceHandle || t.sourceHandle.type !== "library") return;
                  let tracks = library.tracks;
                  if (library.hasHandle && library.status !== "ready") {
                    const ok = await library.reattachFromIdb();
                    if (!ok) return;
                    tracks = library.tracks; // hook may not have re-rendered yet
                  }
                  const match = (tracks || []).find((x) => x.path === t.sourceHandle.path);
                  if (!match) return;
                  const eng = engineRef.current;
                  if (eng?.attachPlaylistFile) eng.attachPlaylistFile(t.trackId, match.file);
                }}
                onLogEvent={(msg) => console.log("[AutoDJ]", msg)}
              />
            </div>
          </div>

        {/* Phase A R3 — Library panel (toggleable). Shown below FX/Auto-DJ. */}
        {!compact && libraryOpen && (
          <div className="mt-3" data-testid="dj-library-wrap">
            <LibraryPanel
              library={library}
              tierAllowsCD={activeLayout === "pro4"}
              onLoadDeck={(t, which) => loadFromLibrary(t, which)}
              onAddToPlaylist={(t) => {
                const eng = engineRef.current; if (!eng?.addToPlaylist) return;
                // Synthesize a stable trackId from path+name for de-dupe.
                const trackId = `lib:${t.path || t.name}`;
                const status = eng.addToPlaylist({
                  trackId,
                  name: t.title || t.name,
                  durationSec: t.durationSec || 0,
                  detectedBpm: t.bpm || 0,
                  detectedKey: t.key || null,
                  file: t.file,
                  sourceHandle: t.path ? { type: "library", folderName: library.folderName, path: t.path } : null,
                });
                // Phase A R7 B2 — surface the de-dupe path with a toast so
                // users understand why nothing happened on the second click.
                // eslint-disable-next-line no-console
                console.log(`[DjConsole2] addToPlaylist '${t.title || t.name}' → ${status}`);
                if (status === "duplicate") {
                  try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "info", msg: "Already in playlist" } })); } catch {}
                }
              }}
            />
          </div>
        )}
      </div>

      {/* macOS Safari unsupported modal */}
      {showSafariModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur" data-testid="safari-unsupported-modal">
          <div className="max-w-md w-[92vw] sr-glass rounded-2xl p-5 border border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h3 className="font-bold">Share Audio isn't supported in Safari on Mac</h3>
            </div>
            <p className="text-sm text-white/70 mb-4">Safari doesn't expose system or tab audio capture. To go live with your external DJ software, use one of:</p>
            <ul className="text-sm text-white/80 list-disc pl-5 space-y-1 mb-4">
              <li>Chrome or Edge on Mac (recommended), <em>with</em> BlackHole for system audio</li>
              <li>Full Spectrum Desktop app — coming in v1.0</li>
            </ul>
            <div className="flex justify-between gap-2">
              <Link to="/help/blackhole-setup" className="px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] bg-[#00F0FF]/15 hover:bg-[#00F0FF]/30 border border-[#00F0FF]/40 rounded-md text-[#00F0FF] flex items-center gap-1.5" data-testid="safari-modal-blackhole-link">
                <ExternalLink className="w-3.5 h-3.5" /> BlackHole setup guide
              </Link>
              <button onClick={() => setShowSafariModal(false)} className="px-3 py-2 text-xs font-bold bg-white/10 hover:bg-white/20 rounded-md" data-testid="safari-modal-close">Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* macOS Chrome one-time tip */}
      {showMobileBlockModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur p-4" data-testid="stagein-mobile-block-modal">
          <div className="max-w-sm w-full sr-glass rounded-2xl p-5 border border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Radio className="w-5 h-5 text-[#FF007F]" />
              <h3 className="font-bold text-base">Share Audio needs a desktop browser</h3>
            </div>
            <p className="text-sm text-white/70 mb-3">
              Capturing system or app audio from another music app (Spotify, Virtual DJ, rekordbox, etc.) isn't possible from a mobile browser — iOS and Android don't expose it.
            </p>
            <div className="mb-3 p-3 rounded-lg bg-white/5 border border-white/10">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] font-bold mb-1">You can still DJ from here</div>
              <p className="text-xs text-white/70">Use the built-in Full Spectrum Decks below. Tap <em>Load A</em> / <em>Load B</em> to pick audio files from your phone (Files app on iOS, Downloads / Music on Android), then play, crossfade, and broadcast just like on desktop.</p>
            </div>
            <div className="mb-4 p-3 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/30">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF007F] font-bold mb-1">Coming in Phase 5</div>
              <p className="text-xs text-white/70">The native Full Spectrum mobile app (iOS &amp; Android) will support full system-audio capture — DJ from any app on your phone, broadcast live to your room.</p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowMobileBlockModal(false)}
                className="px-4 py-2 text-xs font-bold bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] rounded-md uppercase tracking-[0.15em]"
                data-testid="stagein-mobile-block-close">
                Use the decks instead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* macOS Chrome one-time tip */}
      {showMacTip && (
        <div className="fixed bottom-4 right-4 z-40 max-w-sm sr-glass rounded-xl p-3 border border-[#00F0FF]/40 shadow-[0_8px_30px_-4px_rgba(0,240,255,0.3)]" data-testid="mac-chrome-tip">
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-md grid place-items-center bg-[#00F0FF]/20 shrink-0">
              <Radio className="w-3.5 h-3.5 text-[#00F0FF]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] font-bold">Mac · Share Audio tip</div>
              <p className="text-xs text-white/70 mt-1">Chrome on Mac can only capture <em>tab</em> audio. For Virtual DJ / Serato / Rekordbox, install <a href="https://github.com/ExistentialAudio/BlackHole/releases" target="_blank" rel="noreferrer" className="underline text-[#00F0FF]">BlackHole</a> (free, 5 min setup).</p>
              <div className="mt-2 flex gap-2">
                <Link to="/help/blackhole-setup" className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 bg-[#00F0FF]/20 hover:bg-[#00F0FF]/40 rounded">Open guide</Link>
                <button onClick={dismissMacTip} className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 hover:bg-white/10 rounded" data-testid="mac-tip-dismiss">Got it</button>
              </div>
            </div>
            <button onClick={dismissMacTip} className="text-white/40 hover:text-white"><X className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}

      {/* Phase A R9 D — Recording result modal removed alongside the
          duplicate `dj-record-btn`. The single Record Mix button now
          uses the inline "Save mix / Save to room" buttons rendered
          next to the recorder controls, with toast feedback on each
          action. */}
    </div>
  );
}

function DeckPanel({ which, state, otherState, containerRef, engineRef, live, cue, setCue, spanClass = "col-span-12 lg:col-span-5",
                    isMaster, onSetMaster,
  onPlayPause, onCueBtn, onSync, onMatchBpm, onHotCue, onLoopIn, onLoopOut, onLoopToggle, onLoopRoll, onLoopRollRelease, onToggleSlip, onKillEq, onTapTempo, mirror }) {
  const accent = which === "A" ? "#FF007F" : "#00F0FF";
  const [pitch, setPitch] = useState(0);
  const [chan, setChan] = useState(1);
  const deck = () => engineRef.current?.[which === "A" ? "deckA" : "deckB"];

  // jog speed
  const spinDur = state.bpm > 0 ? (60 / state.bpm * 4) : 1.8; // one revolution = 4 beats roughly

  // loop beats display
  const beats = state.loop.start != null && state.loop.end != null && state.bpm > 0
    ? Math.round((state.loop.end - state.loop.start) * (state.bpm / 60)) : null;

  // ---- Jog wheel: click-drag to scrub ----
  const jogRef = useRef(null);
  const scrubRef = useRef({ active: false, wasPlaying: false, startTime: 0, lastAngle: 0, accumDeg: 0 });

  const onJogPointerDown = (e) => {
    const el = jogRef.current; const d = deck();
    if (!el || !d) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const ang = Math.atan2(e.clientY - cy, e.clientX - cx);
    const wasPlaying = d.playing;
    if (wasPlaying) d.pause();
    scrubRef.current = { active: true, wasPlaying, startTime: d.currentTime, lastAngle: ang, accumDeg: 0 };
    try { el.setPointerCapture(e.pointerId); } catch { /* */ }
    el.classList.add("is-scrubbing");
  };
  const onJogPointerMove = (e) => {
    const s = scrubRef.current; if (!s.active) return;
    const el = jogRef.current; const d = deck();
    if (!el || !d) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const ang = Math.atan2(e.clientY - cy, e.clientX - cx);
    let delta = ang - s.lastAngle;
    if (delta >  Math.PI) delta -= 2 * Math.PI;
    if (delta < -Math.PI) delta += 2 * Math.PI;
    s.accumDeg += delta * 180 / Math.PI;
    s.lastAngle = ang;
    // One full disc revolution = 4 beats if BPM is known, else 4 seconds.
    const fullRevSec = state.bpm > 0 ? (60 / state.bpm * 4) : 4;
    const seekDelta = (s.accumDeg / 360) * fullRevSec;
    d.seekTo(s.startTime + seekDelta);
    const spin = el.querySelector(".sr-jog__spin");
    if (spin) spin.style.transform = `rotate(${s.accumDeg}deg)`;
  };
  const onJogPointerUp = (e) => {
    const s = scrubRef.current; if (!s.active) return;
    const el = jogRef.current; const d = deck();
    s.active = false;
    if (el) {
      try { el.releasePointerCapture(e.pointerId); } catch { /* */ }
      el.classList.remove("is-scrubbing");
      const spin = el.querySelector(".sr-jog__spin");
      // Drop the inline transform so the CSS spin animation resumes correctly.
      if (spin) spin.style.transform = "";
    }
    if (s.wasPlaying) d?.play();
  };

  return (
    <div className={`${spanClass} sr-dj-card p-3 ${live ? "sr-deck-active" : ""}`}
      style={{ "--sr-deck-color": accent }} data-testid={`deck-${which}`}>
      <div className={`flex items-center gap-2 mb-2 ${mirror ? "flex-row-reverse" : ""}`}>
        <div className="w-7 h-7 rounded-md grid place-items-center text-[11px] font-black"
          style={{ background: accent, color: "#0A0A0F" }}>{which}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Deck {which}</div>
          <div className="font-semibold truncate text-sm" data-testid={`deck-${which}-title`}>{state.title}</div>
        </div>
        <button onClick={() => setCue(c => !c)} className={`p-1.5 rounded ${cue ? "bg-white/20" : "bg-white/5"} border border-white/10`}
          title="Headphones (visual only)" data-testid={`deck-${which}-headphone`}>
          <Headphones className="w-3.5 h-3.5" style={{ color: cue ? accent : "rgba(255,255,255,0.4)" }} />
        </button>
        <div className="text-right min-w-[5rem]">
          <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Detected</div>
          <div className="font-mono font-black text-base leading-none" data-testid={`dj-bpm-detected-${which}`}
               style={{ color: accent }}>
            {state.bpm ? state.bpm.toFixed(1) : "--"}
          </div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1">Live</div>
          <div className="font-mono font-bold leading-none" style={{ color: accent }} data-testid={`deck-${which}-bpm`}>
            {state.bpm ? (state.bpm * (1 + (state.pitch || 0) / 100)).toFixed(1) : "--"}
          </div>
          {/* Phase A R2 — downbeat 4-dot indicator */}
          <DownbeatIndicator deckId={which} state={state} accent={accent} />
          {/* Phase A R5 F4 — tap-tempo button removed per user request. */}
        </div>
      </div>

      <div className="grid grid-cols-[130px_1fr] gap-3 items-start">
        {/* Jog wheel — drag to scrub. Doesn't toggle play on plain click (per Phase 2.1 spec). */}
        <div
          ref={jogRef}
          className="sr-jog"
          style={{ "--sr-deck-color": accent, "--sr-spin-dur": `${spinDur}s`, touchAction: "none" }}
          onPointerDown={onJogPointerDown}
          onPointerMove={onJogPointerMove}
          onPointerUp={onJogPointerUp}
          onPointerCancel={onJogPointerUp}
          data-testid={`deck-${which}-jog`}>
          <div className={`sr-jog__spin ${state.playing ? "" : "paused"}`} style={{ position: "absolute", inset: 0 }}>
            <div className="sr-jog__marker" />
          </div>
          <div className="sr-jog__inner">
            <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>
              {state.playing ? "▶" : "■"}
            </span>
          </div>
        </div>

        {/* Waveform + transport */}
        <div>
          <div ref={containerRef} className="rounded-md bg-black/40 border border-white/5 mb-2 relative overflow-hidden" data-testid={`deck-${which}-waveform`}>
            {/* Loop region overlay */}
            {state.loop.start != null && state.loop.end != null && state.dur > 0 && (
              <div className="absolute inset-y-0 pointer-events-none" data-testid={`deck-${which}-loop-region`}
                style={{
                  left: `${(state.loop.start / state.dur) * 100}%`,
                  width: `${((state.loop.end - state.loop.start) / state.dur) * 100}%`,
                  background: `${accent}22`, borderLeft: `1px solid ${accent}`, borderRight: `1px solid ${accent}`,
                }} />
            )}
            {/* Hot cue markers */}
            {state.hotCues.map((t, i) => t != null && state.dur > 0 && (
              <div key={i} className="absolute top-0 bottom-0 pointer-events-none"
                style={{ left: `${(t / state.dur) * 100}%`, width: 2, background: accent, opacity: 0.75 }} />
            ))}
          </div>

          <div className="flex items-center gap-1.5 mb-2">
            <button onClick={onCueBtn} className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-xs font-semibold flex items-center gap-1" data-testid={`deck-${which}-cue-btn`}>
              <SkipBack className="w-3.5 h-3.5" /> CUE
            </button>
            <button onClick={onPlayPause}
              className="px-3.5 py-1.5 rounded font-bold text-sm flex items-center gap-1"
              style={{ background: accent, color: "#0A0A0F" }}
              data-testid={`deck-${which}-play-btn`}>
              {state.playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {state.playing ? "PAUSE" : "PLAY"}
            </button>
            <button onClick={onSync}
                    disabled={isMaster}
                    className={`px-2.5 py-1.5 border rounded text-xs font-semibold ${isMaster ? "bg-amber-500/20 border-amber-400/40 text-amber-200 cursor-default" : "bg-white/5 hover:bg-white/10 border-white/10"}`}
                    title={isMaster ? "This deck is the master. Click MASTER to demote." : "Sync this deck's BPM to the master deck's live BPM"}
                    data-testid={`deck-${which}-sync-btn`}>
              {isMaster ? "MASTER" : "SYNC"}
            </button>
            {/* Phase A R10 B — Master toggle. Click to promote this deck
                to master. The master's Sync button becomes a status badge
                ("MASTER") and is disabled. Other decks sync TO this one. */}
            <button onClick={() => onSetMaster?.()}
                    className={`px-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] border ${isMaster ? "bg-amber-500 text-black border-amber-500" : "bg-white/5 hover:bg-amber-500/20 hover:text-amber-200 text-white/60 border-white/10"}`}
                    title={isMaster ? "This is the master deck" : "Make this deck the master (other decks sync to it)"}
                    data-testid={`dj-deck-master-${which}`}>
              {isMaster ? "★" : "☆"}
            </button>
            {/* Phase 3.6 R2 — type a target BPM, hit Match to lock pitch to it. */}
            <MatchBpmInput which={which} state={state} onMatch={onMatchBpm} />
            <div className="ml-auto text-[10px] font-mono text-white/40">{fmt(state.pos)} / {fmt(state.dur)}</div>
          </div>

          {/* Hot cue pads — 8 per deck (Phase 3.6 R2) */}
          <div className="flex items-center gap-1 mb-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-[0.2em] text-white/40 w-12">Cues</span>
            {[0,1,2,3,4,5,6,7].map(i => (
              <button key={i}
                onClick={(e) => onHotCue(i, e.shiftKey ? "clear" : null)}
                onContextMenu={(e) => { e.preventDefault(); onHotCue(i, "clear"); }}
                className={`sr-pad ${state.hotCues[i] != null ? "is-set" : ""}`}
                style={{ "--sr-pad-color": accent }}
                data-testid={`deck-${which}-hotcue-${i+1}`}
                title={state.hotCues[i] != null ? `Jump (shift-click or right-click to clear)` : "Set hot cue"}>
                {i + 1}
              </button>
            ))}
          </div>

          {/* Loop rolls — 8 fractional beats (Phase A R2). Press-and-hold = momentary, click = sustained. */}
          <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid={`dj-loop-rolls-${which}`}>
            <span className="text-[9px] uppercase tracking-[0.2em] text-white/40 w-12">Loops</span>
            {[
              { beats: 1/32, label: "1/32" },
              { beats: 1/16, label: "1/16" },
              { beats: 1/8,  label: "1/8"  },
              { beats: 1/4,  label: "1/4"  },
              { beats: 1/2,  label: "1/2"  },
              { beats: 1,    label: "1"    },
              { beats: 2,    label: "2"    },
              { beats: 4,    label: "4"    },
            ].map(({ beats, label }) => (
              <button key={label}
                onPointerDown={(e) => { e.preventDefault(); onLoopRoll?.(beats, true); }}
                onPointerUp={(e)   => { e.preventDefault(); onLoopRollRelease?.(); }}
                onPointerLeave={() => onLoopRollRelease?.()}
                onContextMenu={(e) => { e.preventDefault(); onLoopRoll?.(beats, false); }}
                className="sr-pad"
                style={{ "--sr-pad-color": accent, minWidth: 28 }}
                data-testid={`dj-loop-roll-${which}-${label}`}
                title={`${label} beat loop — hold for momentary (slip-return), right-click to sustain`}>
                {label}
              </button>
            ))}
            {/* Phase A R3 U2 — explicit Exit Loop button */}
            <button onClick={() => onLoopRollRelease?.()}
                    className={`dj-loop-exit ml-1 ${state.loop?.on ? "is-active" : ""}`}
                    title="Release current loop / loop-roll"
                    data-testid={`dj-loop-exit-${which}`}>
              {state.loop?.on ? "Exit loop" : "No active loop"}
            </button>
          </div>

          {/* Slip mode + EQ kill switches (Phase A R2) */}
          <div className="flex items-center gap-1.5 mb-2">
            <button onClick={() => onToggleSlip?.()}
                    className={`px-2 py-1 rounded text-[10px] uppercase tracking-[0.15em] font-bold border ${state.slip ? "text-black" : "text-white/70 border-white/15 bg-white/5"}`}
                    style={state.slip ? { background: accent, borderColor: accent } : undefined}
                    data-testid={`dj-slip-toggle-${which}`}
                    aria-pressed={!!state.slip}>
              SLIP {state.slip ? "ON" : "OFF"}
            </button>
            <span className="text-[9px] uppercase tracking-[0.2em] text-white/35 ml-2">Kill</span>
            {["low","mid","high"].map((band) => (
              <button key={band}
                onClick={() => onKillEq?.(band)}
                className={`px-1.5 py-1 rounded text-[10px] uppercase tracking-[0.15em] font-bold border ${state.kills?.[band] != null ? "text-black" : "text-white/70 border-white/15 bg-white/5"}`}
                style={state.kills?.[band] != null ? { background: "#ef4444", borderColor: "#ef4444" } : undefined}
                data-testid={`dj-eq-kill-${which}-${band}`}
                aria-pressed={state.kills?.[band] != null}>
                {band[0].toUpperCase()}
              </button>
            ))}
          </div>

          {/* Loop controls */}
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-[0.2em] text-white/40 w-12">Loop</span>
            <button onClick={onLoopIn} className="px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-[10px] font-semibold" data-testid={`deck-${which}-loop-in`}>IN</button>
            <button onClick={onLoopOut} className="px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-[10px] font-semibold" data-testid={`deck-${which}-loop-out`}>OUT</button>
            <button onClick={onLoopToggle}
              className={`px-2 py-1 rounded text-[10px] font-bold ${state.loop.on ? "" : "border border-white/10 bg-white/5"}`}
              style={state.loop.on ? { background: accent, color: "#0A0A0F" } : {}}
              data-testid={`deck-${which}-loop-toggle`}>
              <RotateCcw className="inline w-3 h-3 mr-0.5" />
              LOOP{beats != null ? ` · ${beats} BEATS` : ""}
            </button>
          </div>
        </div>
      </div>

      {/* EQ knobs + filter + gain */}
      <div className="mt-3 grid grid-cols-5 gap-2">
        <Knob label="HI"    accent={accent} bipolar onChange={(v) => deck()?.setHigh(v)}   testid={`deck-${which}-hi`} />
        <Knob label="MID"   accent={accent} bipolar onChange={(v) => deck()?.setMid(v)}    testid={`deck-${which}-mid`} />
        <Knob label="LO"    accent={accent} bipolar onChange={(v) => deck()?.setLow(v)}    testid={`deck-${which}-lo`} />
        <Knob label="FLTR"  accent={accent} bipolar onChange={(v) => deck()?.setFilter(v)} testid={`deck-${which}-filter`} />
        <Knob label="GAIN"  accent={accent} bipolar onChange={(v) => deck()?.setGain(1 + v)} testid={`deck-${which}-gain`} />
      </div>

      <div className="mt-3 grid grid-cols-[1fr_60px] gap-3 items-center">
        <div className="flex flex-col gap-1">
          {/* Phase A R11 E — Beatmatch helper. Hidden if either deck's
              BPM is 0. Green dot + 'in sync' when |effA - effB| < 0.5 BPM,
              red dot + 'off by N BPM' otherwise. Updates live as either
              deck's pitch or BPM changes via React state. */}
          {(() => {
            const mine = state.bpm > 0 ? state.bpm * (1 + (state.pitch || 0) / 100) : 0;
            const theirs = otherState?.bpm > 0 ? otherState.bpm * (1 + (otherState.pitch || 0) / 100) : 0;
            if (!mine || !theirs) return null;
            const delta = Math.abs(mine - theirs);
            const inSync = delta < 0.5;
            return (
              <div data-testid={`dj-beatmatch-${which}`}
                   className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] ${inSync ? "text-emerald-300" : "text-rose-300"}`}
                   title={inSync ? "Deck is beatmatched to the other deck within 0.5 BPM" : `Deck is off by ${delta.toFixed(1)} BPM — click SYNC`}>
                <span className={`w-2 h-2 rounded-full ${inSync ? "bg-emerald-400 animate-pulse" : "bg-rose-400"}`} />
                {inSync ? "in sync" : `off by ${delta.toFixed(1)} BPM`}
              </div>
            );
          })()}
          <div className="flex items-center gap-2">
            <span className="text-[9px] uppercase tracking-[0.2em] text-white/40 w-10">Pitch</span>
            <input type="range" min={-8} max={8} step={0.1} value={pitch}
              onChange={(e) => { const p = parseFloat(e.target.value); setPitch(p); deck()?.setPitch(p); }}
              className="flex-1 sr-xfader" style={{ "--sr-xf-glow": `${accent}66` }}
              data-testid={`deck-${which}-pitch`} />
            <span className="text-[10px] font-mono w-10 text-right" style={{ color: accent }}>{pitch.toFixed(1)}%</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <input type="range" min={0} max={1.2} step={0.01} value={chan} orient="vertical"
            onChange={(e) => { const v = parseFloat(e.target.value); setChan(v); deck()?.setChannel(v); }}
            className="sr-fader-v" style={{ writingMode: "bt-lr", WebkitAppearance: "slider-vertical" }}
            data-testid={`deck-${which}-chan`} />
          <LedColumn level={Math.min(1, vuClampForDeck(engineRef, which))} color={accent} />
        </div>
      </div>
    </div>
  );
}

function MixerPanel({ xfader, setXfader, master, setMaster, vu, spanClass = "col-span-12 lg:col-span-2", showAssigns = false, assigns = {}, onAssignChange = () => {}, autoSyncOnLoad = true, setAutoSyncOnLoad = () => {} }) {
  const xfGlow = xfader < -0.05 ? "#FF007F" : xfader > 0.05 ? "#00F0FF" : "rgba(255,255,255,0.4)";
  return (
    <div className={`${spanClass} bg-black/30 border border-white/10 rounded-xl p-3 flex flex-col items-center gap-3`} data-testid="mixer-panel">
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Master</div>
      <LedRow level={vu.rms * 2} peak={vu.peak} testid="master-vu" />
      <Knob label="VOL" accent="#fff" centerAt={0.5} value={master}
        onChange={(v) => setMaster(v)} range={[0, 1]} testid="master-vol" />
      {/* Phase A R12 — Auto-Sync on Load. Compact toggle in the master
          section. When ON, newly-loaded non-master decks auto-run the
          engine sync path (same as clicking Sync) so beat-matching is
          ready the instant detection completes. */}
      <label
        className="w-full flex items-start gap-2 px-1.5 py-1 rounded border border-white/10 bg-white/[0.04] cursor-pointer select-none"
        title="When ON, any newly-loaded non-master deck auto-runs Sync to match the master's live BPM (clamped ±8%). Default ON."
      >
        <input
          type="checkbox"
          checked={!!autoSyncOnLoad}
          onChange={(e) => setAutoSyncOnLoad(e.target.checked)}
          className="mt-0.5 accent-amber-400"
          data-testid="dj-auto-sync-on-load"
        />
        <span className="text-[10px] leading-snug">
          <span className="block uppercase tracking-[0.18em] text-white/55 font-bold">Auto-Sync</span>
          <span className="block text-white/40">on load</span>
        </span>
      </label>
      <div className="w-full mt-auto">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1 text-center">Crossfader</div>
        <input type="range" min={-1} max={1} step={0.01} value={xfader}
          onChange={(e) => setXfader(parseFloat(e.target.value))}
          className="sr-xfader w-full"
          style={{ "--sr-xf-glow": xfGlow }}
          data-testid="crossfader" />
        <div className="flex justify-between text-[9px] uppercase tracking-[0.2em] mt-1">
          <span style={{ color: "#FF007F" }}>A</span><span style={{ color: "#00F0FF" }}>B</span>
        </div>
      </div>
      {showAssigns && (
        <div className="w-full grid grid-cols-4 gap-1 mt-1" data-testid="dj-xfader-assigns">
          {["A", "B", "C", "D"].map((d) => (
            <label key={d} className="flex flex-col items-center gap-0.5">
              <span className="text-[9px] uppercase tracking-[0.18em] text-white/40">{d}</span>
              <select value={assigns[d] || "thru"} onChange={(e) => onAssignChange(d, e.target.value)}
                      className="bg-white/5 border border-white/10 rounded text-[10px] px-1 py-0.5 text-white w-full"
                      data-testid={`dj-xfader-assign-${d}`}>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="thru">—</option>
              </select>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Phase A R2 — Performance layout.
 * 8×4 hot-cue pad grid for both decks side-by-side, plus loop-roll strips.
 * Built on top of the same engine so hot cues + loops behave identically.
 */
function PerformanceLayout({ deckA, deckB, engineRef, onHotCue, onLoopRoll, onLoopRollRelease, onPlayPause, onClearAllCues, onLoopExit }) {
  return (
    <div className="col-span-12 grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="dj-performance-layout">
      {[
        { which: "A", state: deckA, accent: "#a855f7" },
        { which: "B", state: deckB, accent: "#34d399" },
      ].map(({ which, state, accent }) => (
        <div key={which} className="bg-black/30 border border-white/10 rounded-xl p-3" data-testid={`perf-deck-${which}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-md grid place-items-center text-[11px] font-black"
                 style={{ background: accent, color: "#0A0A0F" }}>{which}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Deck {which}</div>
              <div className="font-semibold truncate text-sm" title={state.title}>{state.title}</div>
            </div>
            <button onClick={() => onPlayPause(which)}
                    className="px-3 py-1.5 rounded font-bold text-xs"
                    style={{ background: accent, color: "#0A0A0F" }}
                    data-testid={`perf-deck-${which}-play`}>
              {state.playing ? "PAUSE" : "PLAY"}
            </button>
            {/* Phase A R3 U1 — Clear all cues */}
            <button onClick={() => onClearAllCues?.(which)}
                    className="px-2 py-1.5 rounded font-bold text-[10px] bg-white/5 hover:bg-white/15 text-white/80 border border-white/10"
                    title="Clear all hot cues on this deck"
                    data-testid={`performance-clear-all-cues-${which}`}>
              Clear all cues
            </button>
          </div>
          <p className="text-[10px] text-white/40 mb-1">
            Right-click any cue or pad to clear. Hold a loop pad to roll; click to engage as a loop.
          </p>
          {/* 8 hot-cue pads with labels (Phase A R3 U1) */}
          <div className="grid grid-cols-4 gap-1.5 mb-2">
            {[0,1,2,3,4,5,6,7].map((i) => (
              <button key={i}
                onClick={(e) => onHotCue(which, i, e.shiftKey ? "clear" : null)}
                onContextMenu={(e) => { e.preventDefault(); onHotCue(which, i, "clear"); }}
                title={state.hotCues[i] != null ? `Cue ${i + 1} · click to jump · right-click to clear` : `Cue ${i + 1} · click to set`}
                className={`sr-pad h-16 text-base flex flex-col items-center justify-center gap-0.5 ${state.hotCues[i] != null ? "is-set" : ""}`}
                style={{ "--sr-pad-color": accent }}
                data-testid={`performance-cue-${which}-${i + 1}`}>
                <span className="leading-none">{i + 1}</span>
                <span className="text-[8px] uppercase tracking-[0.18em] opacity-80 leading-none">
                  Cue {i + 1}
                </span>
              </button>
            ))}
          </div>
          {/* 8 loop-roll strips + visible Exit Loop button (U2) */}
          <div className="grid grid-cols-8 gap-1">
            {[
              { beats: 1/32, label: "1/32" }, { beats: 1/16, label: "1/16" },
              { beats: 1/8,  label: "1/8"  }, { beats: 1/4,  label: "1/4"  },
              { beats: 1/2,  label: "1/2"  }, { beats: 1,    label: "1"    },
              { beats: 2,    label: "2"    }, { beats: 4,    label: "4"    },
            ].map(({ beats, label }) => (
              <button key={label}
                onPointerDown={(e) => { e.preventDefault(); onLoopRoll(which, beats, true); }}
                onPointerUp={(e)   => { e.preventDefault(); onLoopRollRelease(which); }}
                onPointerLeave={() => onLoopRollRelease(which)}
                onContextMenu={(e) => { e.preventDefault(); onLoopRoll(which, beats, false); }}
                className="sr-pad text-[10px] py-2"
                style={{ "--sr-pad-color": accent }}
                data-testid={`perf-${which}-loop-${label}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex justify-end mt-1">
            <button onClick={() => onLoopExit?.(which)}
                    className={`dj-loop-exit ${state.loop?.on ? "is-active" : ""}`}
                    title="Exit current loop / loop-roll"
                    data-testid={`dj-loop-exit-${which}`}>
              {state.loop?.on ? "Exit loop" : "No active loop"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function vuClampForDeck(engineRef, which) {
  const deck = engineRef.current?.[which === "A" ? "deckA" : "deckB"];
  if (!deck) return 0;
  return deck.getLevel().rms * 2; // scale for LED column display
}

// ─────── Knob ───────
function Knob({ label, accent = "#fff", bipolar = false, range = [-1, 1], centerAt, value: extValue, onChange, testid }) {
  const [val, setVal] = useState(extValue != null ? extValue : (bipolar ? 0 : (centerAt ?? (range[0] + range[1]) / 2)));
  const startY = useRef(0);
  const startVal = useRef(0);
  const dragging = useRef(false);

  useEffect(() => { if (extValue != null) setVal(extValue); }, [extValue]);

  const lo = bipolar ? -1 : range[0];
  const hi = bipolar ? 1 : range[1];

  const onPointerDown = (e) => {
    dragging.current = true;
    startY.current = e.clientY;
    startVal.current = val;
    e.target.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dy = startY.current - e.clientY;
    const range = hi - lo;
    let v = startVal.current + (dy / 150) * range;
    if (v < lo) v = lo; if (v > hi) v = hi;
    setVal(v); onChange?.(v);
  };
  const onPointerUp = (e) => { dragging.current = false; e.target.releasePointerCapture?.(e.pointerId); };
  const onWheel = (e) => {
    e.preventDefault();
    const step = (hi - lo) * 0.02 * (e.deltaY < 0 ? 1 : -1);
    let v = val + step; if (v < lo) v = lo; if (v > hi) v = hi;
    setVal(v); onChange?.(v);
  };
  const onDoubleClick = () => { const v = bipolar ? 0 : (centerAt ?? (lo + hi) / 2); setVal(v); onChange?.(v); };
  const onKeyDown = (e) => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const step = (hi - lo) * 0.04 * (e.key === "ArrowUp" ? 1 : -1);
    let v = val + step; if (v < lo) v = lo; if (v > hi) v = hi;
    setVal(v); onChange?.(v);
  };

  // Map val to rotation: -135° to +135°
  const t = (val - lo) / (hi - lo);
  const rot = -135 + t * 270;

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="sr-knob" tabIndex={0} role="slider" aria-valuenow={val.toFixed(2)}
        aria-valuemin={lo} aria-valuemax={hi} aria-label={label}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={onPointerUp} onWheel={onWheel}
        onDoubleClick={onDoubleClick} onKeyDown={onKeyDown}
        style={{ "--sr-knob-color": accent }}
        data-testid={testid}
      >
        <div className="sr-knob__detent" />
        <div className="sr-knob__cap">
          <div className="sr-knob__indicator" style={{ transform: `rotate(${rot}deg)` }} />
        </div>
      </div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-white/50">{label}</div>
    </div>
  );
}

// ─────── LED meters ───────
function LedRow({ level, peak, testid }) {
  const segs = 12;
  const lit = Math.round(level * segs);
  return (
    <div className="w-full flex gap-0.5 h-2" data-testid={testid}>
      {Array.from({ length: segs }, (_, i) => {
        const on = i < lit;
        const color = i < 7 ? "#22ff88" : i < 10 ? "#ffe44a" : "#ff4a4a";
        return (
          <div key={i} className={`sr-led ${on ? "on" : ""}`} style={{ background: on ? color : "rgba(255,255,255,0.05)", color }} />
        );
      })}
    </div>
  );
}
function LedColumn({ level, color }) {
  const segs = 10;
  const lit = Math.round((level || 0) * segs);
  return (
    <div className="flex flex-col-reverse gap-0.5 h-[100px] w-1.5">
      {Array.from({ length: segs }, (_, i) => {
        const on = i < lit;
        return <div key={i} className={`sr-led ${on ? "on" : ""}`} style={{ background: on ? color : "rgba(255,255,255,0.05)", color, height: 4 }} />;
      })}
    </div>
  );
}

function fmt(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60); const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
}

/**
 * Phase A R2 — Downbeat indicator (4 small lights, beat 1 emphasized).
 * Polls deck.beatPhase() at ~30fps and highlights the active beat.
 */
function DownbeatIndicator({ deckId, state, accent }) {
  const [phase, setPhase] = useState(-1);
  useEffect(() => {
    if (!state.bpm || !state.playing) { setPhase(-1); return; }
    let raf;
    const tick = () => {
      // Without engine ref here, approximate from state.pos + state.bpm.
      const beatSec = 60 / (state.bpm * (1 + (state.pitch || 0) / 100));
      const elapsed = (state.pos || 0);
      const idx = Math.floor(elapsed / beatSec) % 4;
      setPhase(idx);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [state.bpm, state.playing, state.pos, state.pitch]);
  return (
    <div className="flex items-center justify-end gap-1 mt-1" data-testid={`dj-downbeat-${deckId}`}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i}
              className={`sr-beat-dot ${i === phase ? "is-on" : ""} ${i === 0 ? "is-down" : ""}`}
              style={i === 0 ? { background: phase === 0 ? accent : undefined } : undefined} />
      ))}
    </div>
  );
}

/**
 * Phase A R2 — Match BPM input.
 * Lets the DJ type a target BPM and lock the deck's pitch to it. Pulls the
 * current effective BPM as a placeholder so the DJ sees what we'd target if
 * they just hit Match without typing anything.
 */
function MatchBpmInput({ which, state, onMatch }) {
  const [value, setValue] = useState(124);
  const submit = (v) => {
    const t = parseFloat(v != null ? v : value);
    if (!isFinite(t) || t < 60 || t > 200) return;
    onMatch?.(t);
  };
  return (
    <span className="inline-flex items-center gap-2 flex-1" data-testid={`deck-${which}-match-group`}>
      <span className="text-[9px] uppercase tracking-[0.18em] text-white/40 w-8 shrink-0">Match</span>
      {/* Phase A R5 F2 — BPM Match slider (60–200, step 0.5) with live readout. */}
      <input
        type="range" min={60} max={200} step={0.5}
        value={value}
        onChange={(e) => { const v = parseFloat(e.target.value); setValue(v); submit(v); }}
        className="flex-1 accent-purple-400"
        data-testid={`dj-match-bpm-slider-${which}`}
        title="Slide to set a target BPM; deck pitch is clamped ±8%." />
      <span className="w-10 text-right font-mono text-[10px] text-white/70" data-testid={`deck-${which}-match-input`}>
        {Number(value).toFixed(1)}
      </span>
      <button onClick={() => submit()}
        className="px-1.5 py-0.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-[10px] uppercase tracking-[0.16em] font-bold"
        data-testid={`deck-${which}-match-btn`}>
        <Target className="inline w-3 h-3 mr-0.5" /> Lock
      </button>
    </span>
  );
}
